/**
 * Test Page for Teacher Content Upload
 * Test các chức năng upload Word và Database
 */

import React, { useState, useEffect, useMemo } from 'react'
import { useAuth } from '../../contexts/AuthContext'
import { teacherContentService } from '../../services/teacher/TeacherContentService'
import { imageExtractor } from '../../services/teacher/ImageExtractor'
import { ContentServiceSimple } from '../../services/content/ContentServiceSimple'
import { lessonService, LessonItem } from '../../services/lesson/LessonService'
import { getYouTubeUrl } from '../../data/youtubeLinks'
import { VideoService } from '../../services/video/VideoService'
import toast from 'react-hot-toast'
import { Upload, Database, FileText, CheckCircle, XCircle, Loader, Eye, Download, Image as ImageIcon, FolderOpen } from 'lucide-react'
import ContentRenderer from '../../components/ContentRenderer/ContentRenderer'
import type { LessonData } from '../../services/teacher/WordToJsonConverter'
import type { ContentItem, BaiHoc } from '../../types'

type SectionKey = 'phanMoDau' | 'phanNoiDung' | 'phanCuoi'

interface TestTeacherContentProps {
  embedded?: boolean // Nếu true, sẽ render với layout compact cho ProfilePage
}

function getSectionTitle(sectionKey: SectionKey): string {
  switch (sectionKey) {
    case 'phanMoDau':
      return 'Phần mở đầu'
    case 'phanNoiDung':
      return 'Phần nội dung'
    case 'phanCuoi':
    default:
      return 'Phần cuối'
  }
}

function isSimulationItem(item?: ContentItem): boolean {
  if (!item) return false
  const simulationTypes = new Set([
    'interactive_question',
    'interactive_exploration',
    'simulation',
    'iframe'
  ])
  return simulationTypes.has(item.type)
}

function isVideoSimulationItem(item?: ContentItem): boolean {
  if (!item) return false
  return item.type === 'video' || isSimulationItem(item)
}

function convertBaiHocToLessonData(lesson: BaiHoc, lop: number, chuong: number, bai: number): LessonData {
  const sections = lesson.cac_phan || []
  const findSection = (id: string, fallbackIndex: number) =>
    sections.find((section) => section.id === id) || sections[fallbackIndex] || sections[sections.length - 1] || null

  const moDau = findSection('mo_dau', 0)
  const noiDung = findSection('noi_dung', 1)
  const phanCuoi = findSection('cuoi', 2)

  return {
    id: Number(lesson.id) || Date.now(),
    tieuDe: lesson.tieu_de || `Bài ${bai}`,
    moTa: lesson.muc_tieu?.join(', ') || '',
    metadata: {
      thoiGian: lesson.thoi_luong || 45,
      doKho: lesson.do_kho || 'trung bình',
      chuDe: lesson.the || [],
      mucTieu: lesson.muc_tieu || []
    },
    noiDung: {
      phanMoDau: {
        tieuDe: moDau?.tieu_de || 'Phần mở đầu',
        noiDung: (moDau?.noi_dung || []) as ContentItem[]
      },
      phanNoiDung: {
        tieuDe: noiDung?.tieu_de || 'Phần nội dung',
        noiDung: (noiDung?.noi_dung || []) as ContentItem[]
      },
      phanCuoi: {
        tieuDe: phanCuoi?.tieu_de || 'Phần cuối',
        noiDung: (phanCuoi?.noi_dung || []) as ContentItem[]
      }
    }
  }
}

async function hydrateContentItemsForPreview(items: ContentItem[]): Promise<ContentItem[]> {
  return Promise.all(
    items.map(async (item) => {
      const cloned: ContentItem = Array.isArray(item.content)
        ? { ...item, content: await hydrateContentItemsForPreview(item.content as ContentItem[]) }
        : { ...item }

      if (item.type === 'image' || item.type === 'video') {
        const rawSrc =
          ((item as any).src as string) ||
          (typeof item.content === 'string' ? (item.content as string) : '')

        if (rawSrc) {
          const resolved = await imageExtractor.resolveMediaSrc(rawSrc)
          if (resolved) {
            if (typeof cloned.content === 'string') {
              cloned.content = resolved
            }
            ; (cloned as any).src = resolved
          }
        }
      }

      return cloned
    })
  )
}

async function hydrateLessonDataForPreview(lesson: LessonData): Promise<LessonData> {
  const cloned = JSON.parse(JSON.stringify(lesson)) as LessonData

  if (cloned.noiDung?.phanMoDau?.noiDung) {
    cloned.noiDung.phanMoDau.noiDung = await hydrateContentItemsForPreview(cloned.noiDung.phanMoDau.noiDung)
  }
  if (cloned.noiDung?.phanNoiDung?.noiDung) {
    cloned.noiDung.phanNoiDung.noiDung = await hydrateContentItemsForPreview(cloned.noiDung.phanNoiDung.noiDung)
  }
  if (cloned.noiDung?.phanCuoi?.noiDung) {
    cloned.noiDung.phanCuoi.noiDung = await hydrateContentItemsForPreview(cloned.noiDung.phanCuoi.noiDung)
  }

  return cloned
}

const TestTeacherContent: React.FC<TestTeacherContentProps> = ({ embedded = false }) => {
  const { user } = useAuth()
  const [uploading, setUploading] = useState(false)
  const [result, setResult] = useState<any>(null)
  const [error, setError] = useState<string | null>(null)
  const [showPreview, setShowPreview] = useState(false)

  // Word upload state
  const [wordFile, setWordFile] = useState<File | null>(null)
  const [lessonId, setLessonId] = useState('lop-11-chuong-1-bai-1')
  const [sharedTitle, setSharedTitle] = useState('Bài học Dao động điều hòa')
  const [sharedLop, setSharedLop] = useState('11')
  const [sharedChuong, setSharedChuong] = useState('1')
  const [sharedBai, setSharedBai] = useState('1')

  const [videoLink, setVideoLink] = useState('')
  const [videoTitle, setVideoTitle] = useState('')
  const [videoDescription, setVideoDescription] = useState('')
  const [simulationSourceType, setSimulationSourceType] = useState<'upload' | 'link'>('upload')
  const [simulationLink, setSimulationLink] = useState('')
  const [simulationFile, setSimulationFile] = useState<File | null>(null)
  const [simulationPreviewUrl, setSimulationPreviewUrl] = useState('')
  const [simulationServerPath, setSimulationServerPath] = useState<string | null>(null)
  const [simulationUploading, setSimulationUploading] = useState(false)
  const [showVideoPreview, setShowVideoPreview] = useState(false)
  const [showSimulationPreview, setShowSimulationPreview] = useState(false)
  const [theorySource, setTheorySource] = useState<'system' | 'custom'>('system')
  const [videoSource, setVideoSource] = useState<'system' | 'custom'>('system')
  const [simulationSource, setSimulationSource] = useState<'system' | 'custom'>('system')
  const [infographicSource, setInfographicSource] = useState<'system' | 'custom'>('system')
  const [infographicFile, setInfographicFile] = useState<File | null>(null)
  const [infographicPreviewUrl, setInfographicPreviewUrl] = useState('')
  const [infographicUploading, setInfographicUploading] = useState(false)
  const [showInfographicPreview, setShowInfographicPreview] = useState(false)
  const [infographicServerPath, setInfographicServerPath] = useState<string | null>(null)
  const [quizSource, setQuizSource] = useState<'system' | 'custom'>('system')
  const [folderUploading, setFolderUploading] = useState(false)
  const [folderUploadMessage, setFolderUploadMessage] = useState<string | null>(null)
  const [questionStats, setQuestionStats] = useState<{ TN: number; DS: number; TL: number } | null>(null)
  const [verifying, setVerifying] = useState(false)
  const [verificationResult, setVerificationResult] = useState<{
    content: { theory: boolean; video: boolean; simulation: boolean; quiz: boolean; infographic: boolean; details: any }
    databases: { TN: { exists: boolean; questionCount: number }; DS: { exists: boolean; questionCount: number }; TL: { exists: boolean; questionCount: number } }
  } | null>(null)
  const [systemLessonData, setSystemLessonData] = useState<LessonData | null>(null)
  const [systemLessonLoading, setSystemLessonLoading] = useState(false)
  const [systemLessonError, setSystemLessonError] = useState<string | null>(null)
  const [previewLessonData, setPreviewLessonData] = useState<LessonData | null>(null)
  const [systemLessonOptions, setSystemLessonOptions] = useState<LessonItem[]>([])
  const [systemLessonOptionsLoading, setSystemLessonOptionsLoading] = useState(false)
  const [systemLessonOptionsError, setSystemLessonOptionsError] = useState<string | null>(null)
  const [selectedSystemLessonId, setSelectedSystemLessonId] = useState<string>('')
  const [imageAssetFiles, setImageAssetFiles] = useState<File[]>([])
  const [youtubeLinkFromMap, setYoutubeLinkFromMap] = useState<string | null>(null)
  const [selectedSystemVideo, setSelectedSystemVideo] = useState<ContentItem | null>(null)
  const [selectedSystemSimulation, setSelectedSystemSimulation] = useState<ContentItem | null>(null)
  const [videoSimulationsFromService, setVideoSimulationsFromService] = useState<ContentItem[]>([])
  const [uploadingAll, setUploadingAll] = useState(false)
  const [uploadProgress, setUploadProgress] = useState<{
    theory: 'pending' | 'uploading' | 'success' | 'error'
    video: 'pending' | 'uploading' | 'success' | 'error'
    simulation: 'pending' | 'uploading' | 'success' | 'error'
    quiz: 'pending' | 'uploading' | 'success' | 'error'
    infographic: 'pending' | 'uploading' | 'success' | 'error'
  }>({
    theory: 'pending',
    video: 'pending',
    simulation: 'pending',
    infographic: 'pending',
    quiz: 'pending'
  })
  const [uploadResults, setUploadResults] = useState<string[]>([])

  useEffect(() => {
    return () => {
      if (simulationPreviewUrl && simulationSourceType === 'upload') {
        URL.revokeObjectURL(simulationPreviewUrl)
      }
    }
  }, [simulationPreviewUrl, simulationSourceType])
  const handleInfographicFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      if (!file.type.startsWith('image/')) {
        toast.error('Vui lòng chọn file ảnh')
        return
      }
      setInfographicFile(file)
      setInfographicServerPath(null) // Reset server path when new file chosen
      // Create preview URL
      const url = URL.createObjectURL(file)
      setInfographicPreviewUrl(url)
      setShowInfographicPreview(true)
    }
  }

  useEffect(() => {
    return () => {
      if (infographicPreviewUrl) {
        URL.revokeObjectURL(infographicPreviewUrl)
      }
    }
  }, [infographicPreviewUrl])

  const handleInfographicSubmit = async () => {
    if (!infographicFile) {
      toast.error('Vui lòng chọn file infographic')
      return
    }

    if (!sharedLop || !sharedChuong || !sharedBai) {
      toast.error('Vui lòng nhập đầy đủ Lớp, Chương và Bài')
      return
    }

    setInfographicUploading(true)
    try {
      // Upload file using uploadInfographic method
      const url = await teacherContentService.uploadInfographic(
        user.id,
        `lop-${sharedLop}-chuong-${sharedChuong}-bai-${sharedBai}`,
        infographicFile
      )

      // Save metadata/content record
      const finalLessonId = `lop-${sharedLop}-chuong-${sharedChuong}-bai-${sharedBai}`

      // ✅ FIX: Use saveMetadata instead of saveCustomContent for infographic
      await teacherContentService.saveMetadata(
        user.id,
        finalLessonId,
        {
          type: 'infographic',
          url: url,
          title: infographicFile.name,
          description: 'Infographic bài học',
          source: 'custom'
        }
      )

      setInfographicServerPath(url)
      toast.success('✅ Upload Infographic thành công')

    } catch (error: any) {
      console.error('❌ Upload Infographic error:', error)
      toast.error(`Lỗi upload: ${error.message}`)
    } finally {
      setInfographicUploading(false)
    }
  }

  const handleFolderUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const fileList = e.target.files
    if (!fileList || fileList.length === 0) {
      toast.error('Vui lòng chọn thư mục Bxx (ví dụ: B06)')
      return
    }

    // ✅ Check user login
    if (!user || !user.id) {
      toast.error('❌ Vui lòng đăng nhập trước khi upload!')
      return
    }

    const files = Array.from(fileList)
    console.log(`📁 [QUIZ TEST] Selected ${files.length} files`)

    // Group files by type (TN/DS/TL)
    const groups: Record<'TN' | 'DS' | 'TL', File | null> = { TN: null, DS: null, TL: null }

    files.forEach((file) => {
      // @ts-ignore
      const relPath = file.webkitRelativePath || file.name
      console.log(`📄 [QUIZ TEST] File: ${relPath}`)

      const segments = relPath.split(/[/\\]/).filter(Boolean)

      // Expected structure: Bxx/TN/questions_tn.json or Bxx/TN/*.json
      if (segments.length < 3) return

      const typeFolder = segments[segments.length - 2].toUpperCase()
      const fileName = segments[segments.length - 1].toLowerCase()

      console.log(`🔍 [QUIZ TEST] Type folder: ${typeFolder}, File: ${fileName}`)

      if (!fileName.endsWith('.json')) return

      if (typeFolder === 'TN' || typeFolder === 'DS' || typeFolder === 'TL') {
        if (!groups[typeFolder]) {
          groups[typeFolder] = file
          console.log(`✅ [QUIZ TEST] Assigned ${fileName} to ${typeFolder}`)
        }
      }
    })

    console.log(`📊 [QUIZ TEST] Groups:`, {
      TN: groups.TN?.name || 'None',
      DS: groups.DS?.name || 'None',
      TL: groups.TL?.name || 'None'
    })

    if (!groups.TN && !groups.DS && !groups.TL) {
      toast.error('Không tìm thấy file JSON trong TN/DS/TL. Cấu trúc: Bxx/TN/*.json')
      return
    }

    // Upload - ✅ PARALLEL UPLOAD for faster speed
    setFolderUploading(true)
    setFolderUploadMessage(null)
    setQuestionStats(null) // Reset stats

    const results: string[] = []
    const errors: string[] = []

    // Stats collector
    const currentStats = { TN: 0, DS: 0, TL: 0 }

    try {
      // ✅ AUTO-DETECT lớp/chương/bài từ first question trong quiz JSON
      let detectedLop = sharedLop
      let detectedChuong = sharedChuong
      let detectedBai = sharedBai

      // Try to read first file to auto-detect
      for (const type of ['TN', 'DS', 'TL'] as const) {
        const file = groups[type]
        if (file) {
          try {
            const text = await file.text()
            const quizData = JSON.parse(text)

            // Get questions array (support multiple formats)
            const questions = quizData.questions ||
              quizData.questions_tn ||
              quizData.questions_ds ||
              quizData.questions_tl || []

            if (questions.length > 0) {
              const firstQ = questions[0]

              // Parse: "L11" → "11", "C2" → "2", "B06" → "06"
              if (firstQ.grade) {
                detectedLop = firstQ.grade.toString().replace(/^L/i, '')
                console.log(`📍 [AUTO-DETECT] Lớp: ${detectedLop} (from ${firstQ.grade})`)
              }
              if (firstQ.chapter) {
                detectedChuong = firstQ.chapter.toString().replace(/^C/i, '')
                console.log(`📍 [AUTO-DETECT] Chương: ${detectedChuong} (from ${firstQ.chapter})`)
              }
              if (firstQ.lesson) {
                detectedBai = firstQ.lesson.toString().replace(/^B/i, '')
                console.log(`📍 [AUTO-DETECT] Bài: ${detectedBai} (from ${firstQ.lesson})`)
              }

              toast.success(`✅ Tự động nhận diện: Lớp ${detectedLop}, Chương ${detectedChuong}, Bài ${detectedBai}`)
              break
            }
          } catch (e) {
            console.warn(`⚠️ Could not auto-detect from ${type}:`, e)
          }
        }
      }

      // ✅ Upload TN, DS, TL in parallel (cùng lúc) with auto-detected values
      const uploadPromises = (['TN', 'DS', 'TL'] as const).map(async (type) => {
        const file = groups[type]
        if (!file) return null

        try {
          console.log(`📤 [QUIZ TEST] Uploading ${type}...`)
          console.log(`   File: ${file.name}, Size: ${file.size} bytes`)

          // Read again for upload (since stream might be consumed if we didn't clone, but text() creates new string so it's fine)
          const text = await file.text()
          const quizData = JSON.parse(text)
          const questionCount = quizData.questions?.length ||
            quizData.questions_tn?.length ||
            quizData.questions_ds?.length ||
            quizData.questions_tl?.length || 0

          console.log(`   Questions: ${questionCount}`)
          currentStats[type] = questionCount

          // ✅ Upload with AUTO-DETECTED lop/chuong/bai
          await teacherContentService.uploadQuizJson(
            user.id,
            detectedLop,
            detectedChuong,
            detectedBai,
            type,
            file
          )

          console.log(`✅ [QUIZ TEST] Success ${type}`)
          return { success: true, type, count: questionCount }
        } catch (typeError: any) {
          console.error(`❌ [QUIZ TEST] Failed ${type}:`, typeError)
          return { success: false, type, error: typeError.message }
        }
      })

      // Wait for all uploads to complete
      const uploadResults = await Promise.all(uploadPromises)

      // Process results
      uploadResults.forEach((result) => {
        if (!result) return

        if (result.success) {
          results.push(`${result.type}: ${result.count} câu`)
        } else {
          errors.push(`${result.type}: ${result.error}`)
        }
      })

      // Update stats and message
      setQuestionStats(currentStats)

      if (errors.length === 0 && results.length > 0) {
        const msg = `✅ Upload thành công: ${results.join(', ')} (L${detectedLop} C${detectedChuong} B${detectedBai})`
        setFolderUploadMessage(msg)
        toast.success(msg)
        setQuizSource('custom')
      } else if (errors.length > 0) {
        const msg = `❌ Có lỗi: ${errors.join('; ')}`
        setFolderUploadMessage(msg)
        toast.error(msg)
      }

    } catch (error: any) {
      console.error('❌ [QUIZ TEST] Error:', error)
      toast.error(error.message)
      setFolderUploadMessage(`❌ Lỗi hệ thống: ${error.message}`)
    } finally {
      setFolderUploading(false)
    }
  }


  const handleImageAssetsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files ? Array.from(e.target.files) : []
    setImageAssetFiles(files)
    if (files.length > 0) {
      toast.success(`Đã chọn ${files.length} file ảnh/minh họa`)
    } else {
      toast('Đã xoá danh sách ảnh minh họa đã chọn')
    }
  }
  useEffect(() => {
    if (showVideoPreview && videoLink.trim() && typeof window !== 'undefined') {
      const timer = setTimeout(() => {
        ; (window as any).forceYouTubeEmbedInit?.()
          ; (window as any).youtubeCspBypassInit?.()
      }, 100)
      return () => clearTimeout(timer)
    }
  }, [showVideoPreview, videoLink])

  useEffect(() => {
    if (theorySource !== 'system') {
      return
    }

    const fetchSystemLessons = async () => {
      if (!sharedLop || !sharedChuong || !sharedBai) {
        setSystemLessonOptions([])
        setSelectedSystemLessonId('')
        setSystemLessonOptionsError('Vui lòng nhập Lớp, Chương và Bài.')
        return
      }

      const lop = parseInt(sharedLop, 10)
      const chuong = parseInt(sharedChuong, 10)
      let bai = sharedBai.trim()
      const baiMatch = bai.match(/\d+/)
      const baiNum = baiMatch ? parseInt(baiMatch[0], 10) : parseInt(bai, 10)

      if ([lop, chuong, baiNum].some((value) => Number.isNaN(value))) {
        setSystemLessonOptions([])
        setSelectedSystemLessonId('')
        setSystemLessonOptionsError('Lớp, Chương hoặc Bài không hợp lệ.')
        return
      }

      setSystemLessonOptionsLoading(true)
      setSystemLessonOptionsError(null)

      try {
        // Chỉ lấy bài học đúng lớp, chương và bài
        const lessons = await lessonService.filterLessons({ lop, chuong, bai: baiNum })
        // Double check để đảm bảo chính xác
        const filteredLessons = lessons.filter(
          (lesson) => lesson.lop === lop && lesson.chuong === chuong && lesson.bai === baiNum
        )

        setSystemLessonOptions(filteredLessons)

        if (filteredLessons.length === 0) {
          setSystemLessonOptionsError(`Không tìm thấy bài học cho Lớp ${lop}, Chương ${chuong}, Bài ${baiNum}.`)
          setSelectedSystemLessonId('')
          return
        }

        // Tự động chọn bài học đã tìm thấy
        const matchedLesson = filteredLessons[0]
        if (matchedLesson) {
          handleSelectSystemLesson(matchedLesson)
        }
      } catch (err: any) {
        console.error('❌ Error loading system lesson list:', err)
        setSystemLessonOptions([])
        setSelectedSystemLessonId('')
        setSystemLessonOptionsError(err?.message || 'Không lấy được danh sách bài hệ thống.')
      } finally {
        setSystemLessonOptionsLoading(false)
      }
    }

    fetchSystemLessons()
  }, [sharedLop, sharedChuong, sharedBai, theorySource])

  useEffect(() => {
    const loadSystemLesson = async () => {
      if (!sharedLop || !sharedChuong || !sharedBai) {
        setSystemLessonData(null)
        return
      }

      const lop = parseInt(sharedLop, 10)
      const chuong = parseInt(sharedChuong, 10)
      const baiMatch = sharedBai.match(/\d+/)
      const bai = baiMatch ? parseInt(baiMatch[0], 10) : parseInt(sharedBai, 10)

      if ([lop, chuong, bai].some((value) => Number.isNaN(value))) {
        setSystemLessonError('Giá trị Lớp/Chương/Bài không hợp lệ.')
        setSystemLessonData(null)
        return
      }

      setSystemLessonLoading(true)
      setSystemLessonError(null)

      try {
        const lesson = await ContentServiceSimple.loadLesson(lop, chuong, bai)
        if (lesson) {
          const converted = convertBaiHocToLessonData(lesson, lop, chuong, bai)
          setSystemLessonData(converted)
        } else {
          setSystemLessonData(null)
          setSystemLessonError('Không tìm thấy nội dung hệ thống cho bài này.')
        }
      } catch (err: any) {
        console.error('❌ Load system lesson error:', err)
        setSystemLessonError(err?.message || 'Không thể tải nội dung hệ thống.')
        setSystemLessonData(null)
      } finally {
        setSystemLessonLoading(false)
      }
    }

    loadSystemLesson()
    // Reset selected simulation khi thay đổi bài học
    setSelectedSystemSimulation(null)
  }, [sharedLop, sharedChuong, sharedBai])

  // Load video mô phỏng từ VideoService (public/videos)
  useEffect(() => {
    if (!sharedLop || !sharedChuong || !sharedBai) {
      setVideoSimulationsFromService([])
      return
    }

    const lop = parseInt(sharedLop.trim(), 10)
    const chuong = parseInt(sharedChuong.trim(), 10)
    let bai = sharedBai.trim()
    const baiMatch = bai.match(/\d+/)
    const baiNum = baiMatch ? parseInt(baiMatch[0], 10) : parseInt(bai, 10)

    if (Number.isNaN(lop) || Number.isNaN(chuong) || Number.isNaN(baiNum)) {
      setVideoSimulationsFromService([])
      return
    }

    // Lấy video mô phỏng từ VideoService
    const videos = VideoService.getVideosByClassAndChapter(lop, chuong)
      .filter(video => video.bai === baiNum)
      .map(video => {
        // Tạo ContentItem từ video
        const videoItem: ContentItem = {
          type: 'video',
          content: video.videoPath, // Đường dẫn dạng /videos/...
          title: video.title || `Mô phỏng - Lớp ${lop}, Chương ${chuong}, Bài ${baiNum}`,
          description: `Video mô phỏng từ hệ thống cho bài học này`,
          isYouTube: false
        } as ContentItem
        return videoItem
      })

    setVideoSimulationsFromService(videos)

    // Tự động chọn video đầu tiên nếu có và chưa có video nào được chọn
    if (videos.length > 0 && !selectedSystemSimulation) {
      setSelectedSystemSimulation(videos[0])
    }
  }, [sharedLop, sharedChuong, sharedBai, selectedSystemSimulation])

  // Load YouTube link từ youtubeLinks.ts
  useEffect(() => {
    if (!sharedLop || !sharedChuong || !sharedBai) {
      setYoutubeLinkFromMap(null)
      return
    }

    const lop = sharedLop.trim()
    const chuong = sharedChuong.trim()
    // Extract số bài từ sharedBai (có thể là "1" hoặc "bai-1" hoặc "bai-1-dao-dong")
    let bai = sharedBai.trim()
    const baiMatch = bai.match(/\d+/)
    if (baiMatch) {
      bai = baiMatch[0]
    }

    const youtubeUrl = getYouTubeUrl(lop, chuong, bai)

    if (youtubeUrl && !youtubeUrl.includes('YOUR_VIDEO_ID')) {
      setYoutubeLinkFromMap(youtubeUrl)
    } else {
      setYoutubeLinkFromMap(null)
    }
  }, [sharedLop, sharedChuong, sharedBai])

  // Tạo video item từ YouTube link khi có link mới
  useEffect(() => {
    if (youtubeLinkFromMap && videoSource === 'system') {
      const lop = sharedLop.trim()
      const chuong = sharedChuong.trim()
      let bai = sharedBai.trim()
      const baiMatch = bai.match(/\d+/)
      if (baiMatch) {
        bai = baiMatch[0]
      }

      const videoItem: ContentItem = {
        type: 'video',
        content: youtubeLinkFromMap,
        title: sharedTitle || `Video bài học - Lớp ${lop}, Chương ${chuong}, Bài ${bai}`,
        description: `Video giảng dạy từ hệ thống cho bài học này`,
        isYouTube: true
      } as ContentItem

      // Chỉ set nếu chưa có video được chọn hoặc video hiện tại không khớp
      setSelectedSystemVideo(prev => {
        if (!prev || prev.content !== youtubeLinkFromMap) {
          return videoItem
        }
        return prev
      })
    }
  }, [youtubeLinkFromMap, sharedLop, sharedChuong, sharedBai, sharedTitle, videoSource])

  useEffect(() => {
    if (videoSource === 'system') {
      setShowVideoPreview(false)
    }
  }, [videoSource])


  useEffect(() => {
    if (quizSource === 'system') {
      setFolderUploadMessage(null)
      setFolderUploading(false)
      setQuestionStats(null) // Reset thống kê khi chuyển sang system
    }
  }, [quizSource])

  const resolvedLessonId = lessonId?.trim() || `lop-${sharedLop}-chuong-${sharedChuong}-bai-${sharedBai}`

  const systemTheorySections = useMemo(() => {
    if (!systemLessonData?.noiDung) return []
    return (['phanMoDau', 'phanNoiDung', 'phanCuoi'] as SectionKey[]).map((key) => ({
      key,
      title: systemLessonData.noiDung?.[key]?.tieuDe || getSectionTitle(key),
      items: (systemLessonData.noiDung?.[key]?.noiDung || []) as ContentItem[]
    }))
  }, [systemLessonData])

  const canonicalLessonData = useMemo(() => {
    if (theorySource === 'custom' && result?.contentData) {
      const cloned = JSON.parse(JSON.stringify(result.contentData)) as LessonData
      return cloned
    }
    if (theorySource === 'system' && systemLessonData) {
      return JSON.parse(JSON.stringify(systemLessonData)) as LessonData
    }
    return (result?.contentData ? JSON.parse(JSON.stringify(result.contentData)) : null) ||
      (systemLessonData ? JSON.parse(JSON.stringify(systemLessonData)) : null) ||
      null
  }, [theorySource, result, systemLessonData])

  useEffect(() => {
    let cancelled = false

    const hydratePreview = async () => {
      if (!canonicalLessonData) {
        if (!cancelled) setPreviewLessonData(null)
        return
      }

      try {
        const hydrated = await hydrateLessonDataForPreview(canonicalLessonData)
        if (!cancelled) {
          setPreviewLessonData(hydrated)
        }
      } catch (error) {
        console.error('❌ Error hydrating preview lesson data:', error)
        if (!cancelled) {
          setPreviewLessonData(canonicalLessonData)
        }
      }
    }

    hydratePreview()

    return () => {
      cancelled = true
    }
  }, [canonicalLessonData])

  // Hàm kiểm tra link YouTube hợp lệ
  const isValidYouTubeLink = (url: string): boolean => {
    if (!url || !url.trim()) return false
    const trimmed = url.trim()
    // Kiểm tra các format YouTube phổ biến
    const youtubePatterns = [
      /^https?:\/\/(www\.)?(youtube\.com\/watch\?v=|youtu\.be\/)/,
      /^https?:\/\/(www\.)?youtube\.com\/embed\//,
      /^https?:\/\/(www\.)?youtube\.com\/v\//
    ]
    return youtubePatterns.some(pattern => pattern.test(trimmed))
  }

  // ✅ NEW: Extract YouTube video ID from URL
  const extractYouTubeId = (url: string): string | null => {
    if (!url) return null
    const trimmed = url.trim()

    // Pattern 1: youtube.com/watch?v=VIDEO_ID
    let match = trimmed.match(/[?&]v=([^&]+)/)
    if (match) return match[1]

    // Pattern 2: youtu.be/VIDEO_ID
    match = trimmed.match(/youtu\.be\/([^?]+)/)
    if (match) return match[1]

    // Pattern 3: youtube.com/embed/VIDEO_ID
    match = trimmed.match(/youtube\.com\/embed\/([^?]+)/)
    if (match) return match[1]

    return null
  }

  const customVideoContentItem = useMemo(() => {
    if (videoSource !== 'custom' || !videoLink.trim()) return null
    const trimmed = videoLink.trim()

    // Chỉ chấp nhận YouTube links
    if (!isValidYouTubeLink(trimmed)) {
      return null
    }

    return {
      type: 'video',
      content: trimmed,
      title: videoTitle || sharedTitle || 'Video bài học',
      description: videoDescription || 'Video do giáo viên cung cấp',
      isYouTube: true
    } as ContentItem
  }, [videoSource, videoLink, videoTitle, videoDescription, sharedTitle])

  const systemVideoItems = useMemo(() => {
    const items: ContentItem[] = []
    console.log('🔍 Debug System Lesson Data:', systemLessonData)

    // Lấy video từ systemLessonData (nội dung bài học)
    systemTheorySections.forEach((section) => {
      console.log(`🔍 Checking section ${section.key}:`, section.items)
      section.items.forEach((item) => {
        if (item?.type === 'video') {
          items.push(item)
        } else if (item?.type === 'section' && Array.isArray(item.content)) {
          // Also check inside nested sections
          item.content.forEach(subItem => {
            if (subItem?.type === 'video') {
              items.push(subItem)
            }
          })
        }
      })
    })

    // Thêm video từ youtubeLinks.ts nếu có
    if (selectedSystemVideo && !items.find(item =>
      item.type === 'video' &&
      (item.content === selectedSystemVideo.content || (item as any).src === selectedSystemVideo.content)
    )) {
      items.push(selectedSystemVideo)
    }

    console.log('🔍 Found System Videos:', items)
    return items
  }, [systemTheorySections, systemLessonData, selectedSystemVideo])

  const previewVideoItems = useMemo(() => {
    if (videoSource === 'custom' && customVideoContentItem) {
      return [customVideoContentItem]
    }
    if (videoSource === 'system') {
      return systemVideoItems
    }
    return []
  }, [videoSource, customVideoContentItem, systemVideoItems])

  const customSimulationContentItem = useMemo(() => {
    if (simulationSource !== 'custom') return null

    // Ưu tiên server path nếu đã upload, fallback về preview URL (blob) hoặc link
    const videoSrc = simulationServerPath || simulationPreviewUrl || simulationLink

    if (!videoSrc) return null

    return {
      type: 'video',
      content: videoSrc,
      src: videoSrc,
      title: simulationFile?.name || 'Video mô phỏng',
      description: 'Video mô phỏng do giáo viên cung cấp',
      isYouTube: simulationLink && (simulationLink.includes('youtube.com') || simulationLink.includes('youtu.be'))
    } as ContentItem
  }, [simulationSource, simulationServerPath, simulationPreviewUrl, simulationLink, simulationFile])

  const systemSimulationItems = useMemo(() => {
    // Ưu tiên video từ VideoService (public/videos)
    if (videoSimulationsFromService.length > 0) {
      return videoSimulationsFromService
    }

    // Fallback: lấy từ systemLessonData nếu không có video từ VideoService
    const items: ContentItem[] = []
    systemTheorySections.forEach((section) => {
      section.items.forEach((item) => {
        // Chỉ lấy video mô phỏng, không lấy interactive_*
        if (isVideoSimulationItem(item)) {
          items.push(item)
        }
      })
    })
    return items
  }, [videoSimulationsFromService, systemTheorySections])

  const previewSimulationItems = useMemo(() => {
    if (simulationSource === 'system') {
      // Nếu có simulation được chọn, dùng nó; nếu không, dùng tất cả
      if (selectedSystemSimulation) {
        return [selectedSystemSimulation]
      }
      return systemSimulationItems
    }
    if (customSimulationContentItem) {
      return [customSimulationContentItem]
    }
    return []
  }, [simulationSource, systemSimulationItems, customSimulationContentItem, selectedSystemSimulation])

  // Tự động chọn simulation đầu tiên khi chuyển sang system source
  useEffect(() => {
    if (simulationSource === 'system') {
      setShowSimulationPreview(false)
      // Tự động chọn simulation đầu tiên nếu có và chưa có simulation nào được chọn
      if (systemSimulationItems.length > 0 && !selectedSystemSimulation) {
        setSelectedSystemSimulation(systemSimulationItems[0])
      }
    }
  }, [simulationSource, systemSimulationItems, selectedSystemSimulation])

  const previewJsonString = useMemo(() => {
    if (theorySource === 'custom' && result?.rawJson) {
      return result.rawJson
    }
    if (canonicalLessonData) {
      return JSON.stringify(canonicalLessonData, null, 2)
    }
    return ''
  }, [canonicalLessonData, theorySource, result])

  const hasPreviewData = Boolean(previewLessonData?.noiDung)
  const canShowVideoPreview = previewVideoItems.length > 0
  const canShowSimulationPreview = previewSimulationItems.length > 0

  // Kiểm tra quyền truy cập
  // Trong dev mode, cho phép tạm thời các email giáo viên test
  const isDevMode = import.meta.env.DEV
  const allowedDevEmails = [
    'tranqquan1982@gmail.com',
    'tranqquants82@gmail.com',
    // TODO: Thêm email tài khoản test của bạn vào đây
  ]
  const hasTeacherRole = user?.role === 'teacher'
  const isAllowedDevUser = isDevMode && user?.email && allowedDevEmails.includes(user.email.toLowerCase())

  if (!user || (!hasTeacherRole && !isAllowedDevUser)) {
    return (
      <div className={embedded ? "bg-white rounded-lg shadow-sm border p-6" : "min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 p-8"}>
        <div className={embedded ? "w-full" : "max-w-4xl mx-auto"}>
          <div className={embedded ? "bg-red-50 rounded-lg p-4 border border-red-200" : "bg-white rounded-lg shadow-lg p-6"}>
            <h1 className={`${embedded ? "text-xl" : "text-2xl"} font-bold text-red-600 mb-4`}>⚠️ Không có quyền truy cập</h1>
            <p className="text-gray-600 mb-4">Chỉ giáo viên mới có thể truy cập trang này.</p>
            {isDevMode && (
              <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-sm text-yellow-800">
                  <strong>Dev Mode:</strong> Để test, vui lòng đăng nhập với một trong các email:
                </p>
                <ul className="list-disc list-inside mt-2 text-xs text-yellow-700">
                  {allowedDevEmails.map(email => (
                    <li key={email}><code className="bg-yellow-100 px-2 py-1 rounded">{email}</code></li>
                  ))}
                </ul>
                <p className="text-xs text-yellow-700 mt-2">
                  Hoặc cấp quyền 'teacher' cho tài khoản của bạn trong WordPress.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    )
  }

  // Log trong dev mode nếu đang dùng dev email
  if (isAllowedDevUser && !hasTeacherRole) {
    console.log('🔧 [Dev Mode] Allowing access for dev email:', user?.email)
  }

  const handleSelectSystemLesson = (lesson: LessonItem) => {
    setSelectedSystemLessonId(lesson.id)
    const baiString = String(lesson.bai)
    if (sharedBai !== baiString) {
      setSharedBai(baiString)
    }
    const generatedLessonId = `lop-${lesson.lop}-chuong-${lesson.chuong}-bai-${lesson.bai}`
    if (lessonId !== generatedLessonId) {
      setLessonId(generatedLessonId)
    }
    if (!sharedTitle?.trim() || theorySource === 'system') {
      setSharedTitle(lesson.tenBai)
    }
  }

  const handleWordUpload = async () => {
    if (!wordFile) {
      toast.error('Vui lòng chọn file Word')
      return
    }

    const finalLessonId = lessonId?.trim() || `lop-${sharedLop}-chuong-${sharedChuong}-bai-${sharedBai}`
    if (!lessonId?.trim()) {
      setLessonId(finalLessonId)
    }
    if (!finalLessonId) {
      toast.error('Vui lòng nhập Lesson ID hoặc thông tin lớp/chương/bài hợp lệ')
      return
    }

    setUploading(true)
    setError(null)
    setResult(null)
    setShowPreview(false) // Reset preview state

    try {
      console.log('📤 Starting Word upload...')
      console.log('📋 File:', wordFile.name, 'Size:', (wordFile.size / 1024).toFixed(2), 'KB')
      console.log('📋 Lesson ID:', lessonId)
      console.log('📋 Teacher ID:', user.id)

      // Upload and convert
      await teacherContentService.uploadTheoryFromWord(
        user.id,
        finalLessonId,
        wordFile,
        imageAssetFiles
      )

      console.log('✅ Upload completed, fetching converted content...')

      // Get converted content to show preview
      const customContent = await teacherContentService.getCustomContent(
        user.id,
        finalLessonId,
        'theory'
      )

      if (!customContent) {
        throw new Error('Không thể lấy nội dung đã convert. Vui lòng thử lại.')
      }

      console.log('✅ Content retrieved:', customContent)
      setResult(customContent)
      setShowPreview(true) // Auto-show preview after successful upload
      toast.success('✅ Upload Word lên host thành công! Nội dung đã được lưu vào database.', { duration: 4000 })
    } catch (err: any) {
      const errorMsg = err?.message || 'Có lỗi xảy ra khi upload Word'
      setError(errorMsg)
      toast.error(`❌ ${errorMsg}`)
      console.error('❌ Upload error:', err)
    } finally {
      setUploading(false)
    }
  }

  const handleWordFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      if (!file.name.endsWith('.docx')) {
        toast.error('File phải là định dạng .docx')
        return
      }
      setWordFile(file)
    }
  }

  const handleSaveCombinedTheory = async () => {
    if (!canonicalLessonData) {
      toast.error('Chưa có nội dung để lưu')
      return
    }
    try {
      await teacherContentService.saveCustomContent(
        user.id,
        resolvedLessonId,
        'theory',
        canonicalLessonData,
        JSON.stringify(canonicalLessonData, null, 2)
      )
      toast.success('✅ Đã lưu nội dung kết hợp lên host thành công!', { duration: 3000 })
    } catch (err: any) {
      console.error('❌ Error saving combined theory:', err)
      toast.error(err?.message || 'Không thể lưu nội dung kết hợp')
    }
  }

  // Upload tổng hợp tất cả nội dung từ 4 bước
  const handleUploadAllContent = async () => {
    if (!sharedLop || !sharedChuong || !sharedBai) {
      toast.error('Vui lòng nhập đầy đủ Lớp, Chương và Bài')
      return
    }

    setUploadingAll(true)
    setUploadResults([])
    setUploadProgress({
      theory: 'pending',
      video: 'pending',
      simulation: 'pending',
      infographic: 'pending',
      quiz: 'pending'
    })

    const results: string[] = []
    const finalLessonId = resolvedLessonId

    try {
      // Bước 1: Lưu Lý thuyết
      if (canonicalLessonData) {
        setUploadProgress(prev => ({ ...prev, theory: 'uploading' }))
        try {
          // ✅ UPDATED: Use saveTheoryJson for Python backend
          await teacherContentService.saveTheoryJson(
            user.id,
            finalLessonId,
            canonicalLessonData
          )
          setUploadProgress(prev => ({ ...prev, theory: 'success' }))
          results.push('✅ Đã lưu Lý thuyết lên host thành công')
          toast.success('✅ Đã upload Lý thuyết lên host thành công!', { duration: 3000 })
        } catch (err: any) {
          setUploadProgress(prev => ({ ...prev, theory: 'error' }))
          const errorMsg = err?.message || 'Unknown error'
          results.push(`❌ Lỗi lưu Lý thuyết: ${errorMsg}`)
          toast.error(`❌ Lỗi upload Lý thuyết: ${errorMsg}`)
          console.error('❌ Error saving theory:', err)
        }
      } else {
        results.push('⚠️ Bỏ qua Lý thuyết (chưa có nội dung)')
      }

      // Bước 2: Lưu Video
      if (videoLink.trim()) {
        setUploadProgress(prev => ({ ...prev, video: 'uploading' }))
        try {
          const videoData = {
            type: 'video',
            url: videoLink.trim(),
            title: videoTitle || sharedTitle || 'Video bài học',
            description: videoDescription || 'Video từ YouTube',
            source: 'custom'
          }
          // ✅ UPDATED: Use saveMetadata for Python backend
          await teacherContentService.saveMetadata(
            user.id,
            finalLessonId,
            videoData
          )
          setUploadProgress(prev => ({ ...prev, video: 'success' }))
          results.push('✅ Đã lưu Video lên host thành công')
          toast.success('✅ Đã upload Video lên host thành công!', { duration: 3000 })
        } catch (err: any) {
          setUploadProgress(prev => ({ ...prev, video: 'error' }))
          results.push(`❌ Lỗi lưu Video: ${err?.message || 'Unknown error'}`)
          toast.error(`❌ Lỗi upload Video: ${err?.message || 'Unknown error'}`)
          console.error('❌ Error saving video:', err)
        }
      } else {
        results.push('⚠️ Bỏ qua Video (chưa chọn)')
      }

      // Bước 3: Lưu Mô phỏng
      let simUrl = null
      if (simulationSourceType === 'upload') {
        simUrl = simulationServerPath
      } else {
        simUrl = simulationLink.trim()
      }

      if (simUrl) {
        setUploadProgress(prev => ({ ...prev, simulation: 'uploading' }))
        try {
          const simulationData = {
            type: 'simulation',
            url: simUrl,
            title: simulationFile?.name || sharedTitle || 'Video mô phỏng',
            description: 'Video mô phỏng tự tải lên',
            source: 'custom'
          }
          // ✅ UPDATED: Use saveMetadata for Python backend
          await teacherContentService.saveMetadata(
            user.id,
            finalLessonId,
            simulationData
          )
          setUploadProgress(prev => ({ ...prev, simulation: 'success' }))
          results.push('✅ Đã lưu Mô phỏng lên host thành công')
          toast.success('✅ Đã upload Mô phỏng lên host thành công!', { duration: 3000 })
        } catch (err: any) {
          setUploadProgress(prev => ({ ...prev, simulation: 'error' }))
          results.push(`❌ Lỗi lưu Mô phỏng: ${err?.message || 'Unknown error'}`)
          toast.error(`❌ Lỗi upload Mô phỏng: ${err?.message || 'Unknown error'}`)
          console.error('❌ Error saving simulation:', err)
        }
      } else {
        results.push('⚠️ Bỏ qua Mô phỏng (chưa chọn)')
      }

      // Bước 4: Ngân hàng câu hỏi
      setUploadProgress(prev => ({ ...prev, quiz: 'uploading' }))
      try {
        if (quizSource === 'custom' && folderUploadMessage) {
          console.log('📤 Uploading quiz databases to Python server...')
          const uploadResults = []

          // ✅ UPDATED: Iterate and use saveQuizJsonToServer
          for (const type of ['TN', 'DS', 'TL'] as const) {
            try {
              console.log(`📤 [QUIZ UPLOAD] Processing ${type} quiz...`)

              // ✅ FIX: Load quiz JSON (not binary database)
              const quizJson = await teacherContentService.loadQuizJsonFromIndexedDB(
                user.id,
                sharedLop,
                sharedChuong,
                sharedBai,
                type
              )

              if (quizJson && quizJson.questions && quizJson.questions.length > 0) {
                console.log(`✅ [QUIZ UPLOAD] Found ${quizJson.questions.length} questions for ${type}`)

                // Save to Python backend using saveQuizJsonToServer
                await teacherContentService.saveQuizJsonToServer(
                  user.id,
                  finalLessonId,
                  type,
                  quizJson  // ✅ Send JSON object, not binary
                )
                uploadResults.push(`${type}: ${quizJson.questions.length} câu`)
                console.log(`✅ [QUIZ UPLOAD] Uploaded ${type} questions (${quizJson.questions.length})`)
              } else {
                console.log(`⚠️ [QUIZ UPLOAD] No ${type} questions found in IndexedDB`)
              }
            } catch (typeError) {
              console.error(`❌ [QUIZ UPLOAD] Error uploading ${type} questions:`, typeError)
            }
          }

          setUploadProgress(prev => ({ ...prev, quiz: 'success' }))
          results.push(`✅ Đã upload Ngân hàng câu hỏi lên server: ${uploadResults.join(', ')}`)
          toast.success('✅ Đã upload Ngân hàng câu hỏi lên host được!', { duration: 3000 })

        } else if (quizSource === 'system') {
          // Optional: Save system quiz preference metadata if needed
          results.push('ℹ️ Sử dụng ngân hàng câu hỏi hệ thống')
          setUploadProgress(prev => ({ ...prev, quiz: 'success' }))
        } else {
          setUploadProgress(prev => ({ ...prev, quiz: 'error' }))
          results.push('⚠️ Bỏ qua Ngân hàng câu hỏi (chưa upload hoặc chưa chọn)')
        }
      } catch (err: any) {
        setUploadProgress(prev => ({ ...prev, quiz: 'error' }))
        results.push(`❌ Lỗi lưu Ngân hàng câu hỏi: ${err?.message || 'Unknown error'}`)
        toast.error(`❌ Lỗi upload Ngân hàng câu hỏi: ${err?.message || 'Unknown error'}`)
        console.error('❌ Error saving quiz:', err)
      }

      // Bước 5: Infographic
      if (infographicServerPath || infographicFile) {
        setUploadProgress(prev => ({ ...prev, infographic: 'uploading' }))
        try {
          console.log('📤 [INFOGRAPHIC UPLOAD] Processing infographic...')

          const infographicData = {
            type: 'infographic',
            url: infographicServerPath,
            title: infographicFile?.name || 'Infographic',
            description: 'Infographic bài học',
            source: 'custom'
          }

          await teacherContentService.saveMetadata(
            user.id,
            finalLessonId,
            infographicData
          )

          setUploadProgress(prev => ({ ...prev, infographic: 'success' }))
          results.push('✅ Đã lưu Infographic lên host thành công')
          toast.success('✅ Đã upload Infographic lên host thành công!', { duration: 3000 })
        } catch (err: any) {
          setUploadProgress(prev => ({ ...prev, infographic: 'error' }))
          results.push(`❌ Lỗi lưu Infographic: ${err?.message || 'Unknown error'}`)
          toast.error(`❌ Lỗi upload Infographic: ${err?.message || 'Unknown error'}`)
          console.error('❌ Error saving infographic:', err)
        }
      } else {
        results.push('⚠️ Bỏ qua Infographic (chưa chọn)')
      }

      setUploadResults(results)

      // Hiển thị kết quả tổng hợp
      const successCount = results.filter(r => r.startsWith('✅')).length
      const errorCount = results.filter(r => r.startsWith('❌')).length

      if (errorCount === 0 && successCount > 0) {
        toast.success(`✅ Đã upload thành công ${successCount} mục!`)
      } else if (successCount > 0) {
        toast.error(`⚠️ Upload hoàn tất: ${successCount} thành công, ${errorCount} lỗi`)
      }

    } catch (err: any) {
      console.error('❌ Error in upload all content:', err)
      toast.error(`❌ Lỗi khi upload: ${err?.message || 'Unknown error'}`)
    } finally {
      setUploadingAll(false)
    }
  }



  const handleSimulationFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      // Validate file type
      if (!file.type.startsWith('video/')) {
        toast.error('File phải là định dạng video (mp4, webm, etc.)')
        return
      }

      // Revoke old blob URL
      if (simulationPreviewUrl && simulationSourceType === 'upload') {
        URL.revokeObjectURL(simulationPreviewUrl)
      }

      setSimulationFile(file)
      setSimulationLink('')
      setSimulationServerPath(null)
      setSimulationUploading(true)

      // Create preview URL
      const objectUrl = URL.createObjectURL(file)
      setSimulationPreviewUrl(objectUrl)

      try {
        // Generate short random filename
        const ext = file.name.split('.').pop() || 'mp4'
        const randomStr = Math.random().toString(36).substring(2, 8)
        const shortFilename = `${randomStr}.${ext}`

        // Upload file to server
        const lessonKey = `lop-${sharedLop}-chuong-${sharedChuong}-bai-${sharedBai}`
        const serverPath = await imageExtractor.saveFetchedBlobToTeacherDirectory(
          file,
          shortFilename,
          user.id,
          lessonKey,
          objectUrl
        )

        setSimulationServerPath(serverPath)
        toast.success('✅ Đã upload video mô phỏng lên server thành công!', { duration: 3000 })
      } catch (error: any) {
        console.error('❌ Error uploading simulation video:', error)
        toast.error(`❌ Lỗi upload video: ${error?.message || 'Unknown error'}`)
        // Keep blob URL for preview even if upload fails
      } finally {
        setSimulationUploading(false)
      }
    }
  }

  const handleVideoLinkSave = async () => {
    if (!videoLink.trim()) {
      toast.error('Vui lòng nhập link YouTube')
      return
    }
    if (!isValidYouTubeLink(videoLink)) {
      toast.error('Link không hợp lệ. Vui lòng nhập link YouTube')
      return
    }

    try {
      setUploading(true)
      const finalLessonId = resolvedLessonId
      const videoData = {
        type: 'video',
        url: videoLink.trim(),
        title: videoTitle || sharedTitle || 'Video bài học',
        description: videoDescription || 'Video từ YouTube',
        source: 'custom'
      }

      await teacherContentService.saveMetadata(
        user.id,
        finalLessonId,
        videoData
      )

      toast.success('✅ Đã lưu link Video lên server thành công!', { duration: 3000 })
    } catch (error: any) {
      console.error('❌ Error saving video link:', error)
      toast.error(`❌ Lỗi lưu video: ${error?.message || 'Unknown error'}`)
    } finally {
      setUploading(false)
    }
  }

  const handleSimulationSubmit = async () => {
    if (simulationSourceType === 'upload' && !simulationFile && !simulationServerPath) {
      toast.error('Vui lòng chọn file video mô phỏng')
      return
    }
    if (simulationSourceType === 'link' && !simulationLink.trim()) {
      toast.error('Vui lòng nhập link video mô phỏng')
      return
    }

    try {
      setSimulationUploading(true)
      const finalLessonId = resolvedLessonId
      let simUrl = null

      if (simulationSourceType === 'link') {
        const link = simulationLink.trim()
        if (link.includes('youtube.com') || link.includes('youtu.be')) {
          setSimulationPreviewUrl(link)
          simUrl = link
        } else {
          toast.error('Vui lòng nhập link YouTube hợp lệ')
          return
        }
      } else {
        // Source type 'upload'
        if (simulationServerPath) {
          simUrl = simulationServerPath
        } else {
          // Should have been uploaded by handleSimulationFileChange
          toast.error('Vui lòng đợi upload file hoàn tất hoặc chọn file lại')
          return
        }
      }

      // Save metadata to server
      if (simUrl) {
        const simulationData = {
          type: 'simulation',
          url: simUrl,
          title: sharedTitle || 'Video mô phỏng',
          description: 'Video mô phỏng',
          source: 'custom'
        }

        await teacherContentService.saveMetadata(
          user.id,
          finalLessonId,
          simulationData
        )
        toast.success('✅ Đã lưu Mô phỏng lên server thành công!', { duration: 3000 })
      }

    } catch (error: any) {
      console.error('❌ Error saving simulation:', error)
      toast.error(`❌ Lỗi lưu mô phỏng: ${error?.message || 'Unknown error'}`)
    } finally {
      setSimulationUploading(false)
    }
  }

  const buildLessonUrl = (withTeacher = true) => {
    const base = `/lop/${sharedLop}/${'chuong'}/${sharedChuong}/bai/${sharedBai}`
    if (withTeacher) {
      return `${base}?teacher=${user.id}`
    }
    return base
  }

  const handleOpenLessonTabs = (withTeacher = true) => {
    const url = `/lop/${sharedLop}/chuong/${sharedChuong}/bai/${sharedBai}${withTeacher ? `?teacher=${user.id}` : ''}`
    window.open(url, '_blank', 'noopener,noreferrer')
  }

  const handlePreviewQuestions = () => {
    // Nếu đã upload thành công (có folderUploadMessage) hoặc quizSource là 'custom', dùng teacher content
    const useTeacherContent = quizSource === 'custom' || (folderUploadMessage && folderUploadMessage.includes('Đã upload'))
    const url = `/trac-nghiem/${sharedLop}/${sharedChuong}/${sharedBai}${useTeacherContent ? `?teacher=${user.id}` : ''}`
    console.log('🔍 Preview questions:', { quizSource, folderUploadMessage, useTeacherContent, url })
    window.open(url, '_blank', 'noopener,noreferrer')
  }

  const handleDownloadJson = () => {
    if (!canonicalLessonData) {
      toast.error('Chưa có nội dung để tải')
      return
    }

    const jsonString = JSON.stringify(canonicalLessonData, null, 2)
    const blob = new Blob([jsonString], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const anchor = document.createElement('a')
    const fileName = `${resolvedLessonId || 'teacher-lesson'}-noi-dung.json`

    anchor.href = url
    anchor.download = fileName
    document.body.appendChild(anchor)
    anchor.click()
    document.body.removeChild(anchor)
    URL.revokeObjectURL(url)

    toast.success(`✅ Đã tạo file ${fileName}`)
  }

  const getDefaultJsonPath = () => {
    const match = resolvedLessonId.match(/lop-(\d+)-chuong-(\d+)-bai-(\d+)/)
    if (match) {
      const [, lopValue, chuongValue, baiValue] = match
      return `src/du-lieu/noi-dung/lop-${lopValue}/chuong-${chuongValue}/bai-${baiValue}/noi-dung.json`
    }
    return 'src/du-lieu/noi-dung/<lop>/<chuong>/<bai>/noi-dung.json'
  }

  return (
    <div className={embedded ? "bg-transparent" : "min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 p-8"}>
      <div className={embedded ? "w-full" : "max-w-6xl mx-auto"}>
        <div className={embedded ? "mb-6 space-y-4" : "mb-8 space-y-6"}>
          {!embedded && (
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                🧪 Test Upload Nội Dung Giáo Viên
              </h1>
              <p className="text-gray-600">
                Quản lý toàn bộ nội dung cá nhân hóa (Lý thuyết, Video, Mô phỏng, Ngân hàng câu hỏi)
              </p>
            </div>
          )}
          {embedded && (
            <div className="mb-4">
              <h2 className="text-2xl font-bold text-gray-900 mb-1">
                Quản lý nội dung bài học
              </h2>
              <p className="text-sm text-gray-600">
                Upload và quản lý nội dung cá nhân hóa cho học sinh
              </p>
            </div>
          )}

          <div className="bg-white rounded-2xl shadow-lg border border-blue-100 p-6">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-900">Thông tin chung cho bài học</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Lớp</label>
                <input
                  type="text"
                  value={sharedLop}
                  onChange={(e) => setSharedLop(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Chương</label>
                <input
                  type="text"
                  value={sharedChuong}
                  onChange={(e) => setSharedChuong(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Bài</label>
                <input
                  type="text"
                  value={sharedBai}
                  onChange={(e) => setSharedBai(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tiêu đề chung</label>
                <input
                  type="text"
                  value={sharedTitle}
                  onChange={(e) => setSharedTitle(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>

          {/* Instructions */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-blue-900 mb-4">📝 Hướng dẫn</h3>
            <div className="space-y-2 text-blue-800">
              <p><strong>Word Upload:</strong></p>
              <ul className="list-disc list-inside ml-4 space-y-1">
                <li>File phải là định dạng .docx</li>
                <li>Nhập Lesson ID theo format: <code className="bg-gray-200 px-1 rounded text-gray-800">lop-{sharedLop}-chuong-{sharedChuong}-bai-{sharedBai}</code></li>
                <li>Hệ thống sẽ tự động extract images/videos từ links trong Word</li>
                <li>Convert Word sang JSON theo cấu trúc ContentItem</li>
              </ul>
              <p className="mt-4"><strong>Database Upload:</strong></p>
              <ul className="list-disc list-inside ml-4 space-y-1">
                <li>Chỉ upload thư mục bài Bxx có đủ 3 thư mục con TN/DS/TL</li>
                <li>Mỗi thư mục con cần có file <code className="bg-gray-200 px-1 rounded text-gray-800">question.db</code></li>
                <li>Hệ thống tự động tải ảnh về local và cập nhật đường dẫn</li>
                <li>Nhấn "Mở giao diện làm bài" để test trực tiếp trên trang trắc nghiệm</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="space-y-6">
          {/* Word Upload Section */}
          <div className="bg-white rounded-lg shadow-lg p-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-semibold tracking-wide text-blue-600 uppercase">Bước 1 · Lý thuyết</p>
              <FileText className="w-6 h-6 text-blue-600" />
            </div>
            <p className="text-sm text-gray-600 mb-4">
              Upload Word (.docx) → Convert thành <code className="bg-gray-200 px-1 rounded text-gray-800">noi-dung.json</code>
            </p>

            <div className="space-y-4">
              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">Nguồn nội dung</p>
                <div className="flex flex-wrap gap-4 text-sm text-gray-700">
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="theory-source"
                      value="system"
                      checked={theorySource === 'system'}
                      onChange={() => setTheorySource('system')}
                    />
                    Dùng nội dung hệ thống
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="theory-source"
                      value="custom"
                      checked={theorySource === 'custom'}
                      onChange={() => setTheorySource('custom')}
                    />
                    Upload nội dung giáo viên
                  </label>
                </div>
              </div>

              {theorySource === 'system' ? (
                <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 text-sm text-blue-800 space-y-3">
                  <div>
                    <p className="font-medium text-blue-900">Nội dung hệ thống</p>
                    {sharedLop && sharedChuong && sharedBai && (
                      <p className="text-xs text-blue-700 mt-1">
                        Đang tải bài học: <strong>Lớp {sharedLop}, Chương {sharedChuong}, Bài {sharedBai}</strong>
                      </p>
                    )}
                  </div>
                  {systemLessonOptionsLoading && <p>Đang tải bài học...</p>}
                  {systemLessonOptionsError && (
                    <p className="text-red-600">{systemLessonOptionsError}</p>
                  )}
                  {!systemLessonOptionsLoading && !systemLessonOptionsError && systemLessonOptions.length > 0 && (
                    <div className="bg-white rounded-lg border border-blue-200 p-3">
                      {systemLessonOptions.map((lesson) => (
                        <div key={lesson.id} className="flex items-start gap-3">
                          <div className="flex-1">
                            <p className="text-sm font-semibold text-blue-900">{lesson.tenBai}</p>
                            <p className="text-xs text-blue-700 mt-1">
                              Lớp {lesson.lop} · Chương {lesson.chuong} · Bài {lesson.bai}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  {!systemLessonOptionsLoading && !systemLessonOptionsError && systemLessonOptions.length === 0 && sharedLop && sharedChuong && sharedBai && (
                    <p className="text-amber-700 bg-amber-50 p-3 rounded border border-amber-200">
                      Không tìm thấy bài học cho Lớp {sharedLop}, Chương {sharedChuong}, Bài {sharedBai}. Vui lòng kiểm tra lại thông tin đã nhập.
                    </p>
                  )}
                  {!sharedLop || !sharedChuong || !sharedBai ? (
                    <p className="text-amber-700 bg-amber-50 p-3 rounded border border-amber-200">
                      Vui lòng nhập đầy đủ Lớp, Chương và Bài ở phần "Thông tin chung cho bài học" phía trên.
                    </p>
                  ) : null}
                  <p className="text-xs text-blue-700">
                    * Nội dung xem trước và file JSON sẽ dùng đúng bài của hệ thống theo Lớp, Chương, Bài đã nhập.
                  </p>
                </div>
              ) : (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Lesson ID
                    </label>
                    <input
                      type="text"
                      value={lessonId}
                      onChange={(e) => setLessonId(e.target.value)}
                      placeholder="lop-11-chuong-1-bai-1"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Chọn file Word (.docx)
                    </label>
                    <button
                      type="button"
                      onClick={() => {
                        const link = document.createElement('a')
                        link.href = '/templates/DaoDongDieuHoa.docx'
                        link.download = 'DaoDongDieuHoa.docx'
                        document.body.appendChild(link)
                        link.click()
                        document.body.removeChild(link)
                        toast.success('✅ Đã tải file mẫu DaoDongDieuHoa.docx')
                      }}
                      className="w-full mb-4 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white font-semibold py-3 px-4 rounded-lg border-2 border-blue-400 shadow-md hover:shadow-lg transition-all duration-200 flex items-center justify-center gap-2"
                    >
                      <Download className="w-5 h-5" />
                      <span>Tải file mẫu DaoDongDieuHoa.docx</span>
                    </button>
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-blue-500 transition-colors">
                      <input
                        type="file"
                        accept=".docx"
                        onChange={handleWordFileChange}
                        className="hidden"
                        id="word-upload"
                      />
                      <label
                        htmlFor="word-upload"
                        className="cursor-pointer flex flex-col items-center gap-2"
                      >
                        <Upload className="w-8 h-8 text-gray-400" />
                        <span className="text-sm text-gray-600">
                          {wordFile ? wordFile.name : 'Click để chọn file Word'}
                        </span>
                      </label>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Thư mục ảnh/video tham chiếu (tùy chọn)
                    </label>
                    <div className="border-2 border-dashed border-gray-200 rounded-lg p-4 text-center hover:border-blue-400 transition-colors">
                      <input
                        type="file"
                        multiple
                        onChange={handleImageAssetsChange}
                        className="hidden"
                        id="image-assets-upload"
                        {...{ webkitdirectory: '' }}
                      />
                      <label
                        htmlFor="image-assets-upload"
                        className="cursor-pointer flex flex-col items-center gap-2"
                      >
                        <Upload className="w-6 h-6 text-gray-400" />
                        <span className="text-sm text-gray-600">
                          Chọn thư mục chứa ảnh/video tham chiếu (ví dụ D:\Teacher_content\images)
                        </span>
                        <span className="text-xs text-gray-500">
                          Hệ thống sẽ dùng tên file để ghép với các đường dẫn nội bộ trong Word
                        </span>
                      </label>
                    </div>
                    {imageAssetFiles.length > 0 && (
                      <p className="text-xs text-blue-700 mt-2">
                        Đã chọn {imageAssetFiles.length} file ảnh/video để ghép đường dẫn.
                      </p>
                    )}
                  </div>

                  <button
                    onClick={handleWordUpload}
                    disabled={uploading || !wordFile}
                    className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {uploading ? (
                      <>
                        <Loader className="w-4 h-4 animate-spin" />
                        Đang xử lý...
                      </>
                    ) : (
                      <>
                        <FileText className="w-4 h-4" />
                        Upload Word
                      </>
                    )}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Result Section */}
        {(previewLessonData || error) && (
          <div className="mt-6 bg-white rounded-lg shadow-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Nội dung bài học</h3>

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                <div className="flex items-center gap-2 text-red-800">
                  <XCircle className="w-5 h-5" />
                  <span className="font-medium">Lỗi</span>
                </div>
                <p className="text-red-600 mt-2">{error}</p>
              </div>
            )}

            {previewLessonData && (
              <div className="bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-300 rounded-xl p-6 shadow-lg">
                <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-green-600 rounded-full flex items-center justify-center shadow-md">
                      <CheckCircle className="w-7 h-7 text-white" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-green-900">
                        {theorySource === 'system' ? 'Đang dùng nội dung hệ thống' : 'Đã convert nội dung giáo viên'}
                      </h3>
                      <p className="text-sm text-green-700">
                        {theorySource === 'system'
                          ? 'Toàn bộ nội dung noi-dung.json mặc định sẽ hiển thị cho học sinh.'
                          : 'File Word đã convert chuẩn cấu trúc noi-dung.json.'}
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-3">
                    <button
                      onClick={() => setShowPreview(!showPreview)}
                      disabled={!hasPreviewData}
                      className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-all shadow-md disabled:bg-gray-300 disabled:cursor-not-allowed"
                    >
                      <Eye className="w-4 h-4" />
                      <span className="font-medium">{showPreview ? 'Ẩn Preview' : 'Xem Preview'}</span>
                    </button>
                    <button
                      onClick={handleDownloadJson}
                      disabled={!canonicalLessonData}
                      className="flex items-center gap-2 px-4 py-2 bg-white text-green-700 border border-green-400 rounded-lg hover:bg-green-100 transition-all shadow-md disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      <Download className="w-4 h-4" />
                      <span className="font-medium">Tải noi-dung.json</span>
                    </button>
                    <button
                      onClick={handleSaveCombinedTheory}
                      disabled={!canonicalLessonData}
                      className="flex items-center gap-2 px-4 py-2 bg-white text-green-700 border border-green-400 rounded-lg hover:bg-green-100 transition-all shadow-md disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      <FileText className="w-4 h-4" />
                      <span className="font-medium">Lưu nội dung này</span>
                    </button>
                  </div>
                </div>

                {showPreview && hasPreviewData && (
                  <div className="mt-6 bg-gradient-to-br from-white via-blue-50 to-indigo-50 rounded-2xl p-6 sm:p-8 border-2 border-green-300 shadow-xl">
                    <div className="flex items-center gap-3 mb-6 pb-4 border-b-2 border-blue-200">
                      <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg">
                        <Eye className="w-7 h-7 text-white" />
                      </div>
                      <div>
                        <h4 className="text-xl sm:text-2xl font-bold text-gray-900">📄 Xem trước nội dung</h4>
                        <p className="text-sm text-gray-600 mt-1">
                          Render bằng ContentRenderer giống học sinh sẽ nhìn thấy
                        </p>
                      </div>
                    </div>

                    <div className="space-y-6 sm:space-y-8">
                      {previewLessonData.noiDung?.phanMoDau?.noiDung &&
                        previewLessonData.noiDung.phanMoDau.noiDung.length > 0 && (
                          <div className="bg-white rounded-xl p-5 sm:p-6 shadow-lg border-l-4 border-blue-500 hover:shadow-xl transition-shadow">
                            <div className="flex items-center gap-3 mb-4 pb-3 border-b border-blue-100">
                              <div className="w-3 h-3 bg-blue-500 rounded-full animate-pulse"></div>
                              <h5 className="text-lg sm:text-xl font-bold text-blue-700">
                                {previewLessonData.noiDung.phanMoDau.tieuDe || 'Phần mở đầu'}
                              </h5>
                            </div>
                            <div className="prose prose-sm sm:prose-base prose-blue max-w-none prose-headings:text-blue-900 prose-p:text-gray-700 prose-strong:text-blue-800 prose-img:rounded-lg prose-img:shadow-md">
                              <ContentRenderer
                                content={previewLessonData.noiDung.phanMoDau.noiDung}
                                onError={(err) => console.error('Preview error:', err)}
                              />
                            </div>
                          </div>
                        )}

                      {previewLessonData.noiDung?.phanNoiDung?.noiDung &&
                        previewLessonData.noiDung.phanNoiDung.noiDung.length > 0 && (
                          <div className="bg-white rounded-xl p-5 sm:p-6 shadow-lg border-l-4 border-green-500 hover:shadow-xl transition-shadow">
                            <div className="flex items-center gap-3 mb-4 pb-3 border-b border-green-100">
                              <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                              <h5 className="text-lg sm:text-xl font-bold text-green-700">
                                {previewLessonData.noiDung.phanNoiDung.tieuDe || 'Phần nội dung'}
                              </h5>
                            </div>
                            <div className="prose prose-sm sm:prose-base prose-green max-w-none prose-headings:text-green-900 prose-p:text-gray-700 prose-strong:text-green-800 prose-img:rounded-lg prose-img:shadow-md">
                              <ContentRenderer
                                content={previewLessonData.noiDung.phanNoiDung.noiDung}
                                onError={(err) => console.error('Preview error:', err)}
                              />
                            </div>
                          </div>
                        )}

                      {previewLessonData.noiDung?.phanCuoi?.noiDung &&
                        previewLessonData.noiDung.phanCuoi.noiDung.length > 0 && (
                          <div className="bg-white rounded-xl p-5 sm:p-6 shadow-lg border-l-4 border-purple-500 hover:shadow-xl transition-shadow">
                            <div className="flex items-center gap-3 mb-4 pb-3 border-b border-purple-100">
                              <div className="w-3 h-3 bg-purple-500 rounded-full animate-pulse"></div>
                              <h5 className="text-lg sm:text-xl font-bold text-purple-700">
                                {previewLessonData.noiDung.phanCuoi.tieuDe || 'Phần cuối'}
                              </h5>
                            </div>
                            <div className="prose prose-sm sm:prose-base prose-purple max-w-none prose-headings:text-purple-900 prose-p:text-gray-700 prose-strong:text-purple-800 prose-img:rounded-lg prose-img:shadow-md">
                              <ContentRenderer
                                content={previewLessonData.noiDung.phanCuoi.noiDung}
                                onError={(err) => console.error('Preview error:', err)}
                              />
                            </div>
                          </div>
                        )}
                    </div>
                  </div>
                )}

                <div className="mt-6 bg-white rounded-xl p-5 border border-dashed border-green-400 shadow-sm">
                  <h5 className="text-base font-semibold text-green-800 mb-3 flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    Hướng dẫn sử dụng file <code className="bg-green-100 px-1 rounded text-green-800">noi-dung.json</code>
                  </h5>
                  <ol className="list-decimal list-outside space-y-2 pl-5 text-sm text-gray-700">
                    <li>Nhấn <strong>Tải noi-dung.json</strong> để lấy file đúng cấu trúc.</li>
                    <li>
                      Sao chép file vào thư mục bài học tương ứng, ví dụ:{' '}
                      <code className="bg-gray-100 px-1 rounded text-gray-800">{getDefaultJsonPath()}</code>.
                    </li>
                    <li>Giữ nguyên tên file <code className="bg-gray-200 px-1 rounded text-gray-800">noi-dung.json</code>.</li>
                    <li>Học sinh vào trang học sẽ thấy nội dung theo lựa chọn của giáo viên.</li>
                  </ol>
                </div>

                <div className="mt-4">
                  <details className="bg-gray-100 rounded-lg">
                    <summary className="cursor-pointer p-3 text-sm font-medium text-gray-700 hover:bg-gray-200 rounded-lg">
                      📋 Xem JSON (Click để mở/đóng)
                    </summary>
                    <pre className="p-4 overflow-auto max-h-96 text-xs">{previewJsonString}</pre>
                  </details>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Video & Simulation Upload (UI only for now) */}
        <div className="mt-8 space-y-6">
          {/* Video Link Upload */}
          <section className="bg-white rounded-xl shadow-lg border border-blue-100 p-6">
            <div className="flex items-center gap-3 mb-2">
              <span className="text-2xl">🎥</span>
              <p className="text-xs font-semibold tracking-wide text-blue-600 uppercase">Bước 2 · Video bài học</p>
            </div>
            <p className="text-sm text-gray-600 mb-4">Chọn video mặc định hoặc nhập link YouTube riêng cho tab Video.</p>

            <div className="space-y-4">
              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">Nguồn video</p>
                <div className="flex flex-wrap gap-4 text-sm text-gray-700">
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="video-source"
                      value="system"
                      checked={videoSource === 'system'}
                      onChange={() => setVideoSource('system')}
                    />
                    Dùng video hệ thống
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="video-source"
                      value="custom"
                      checked={videoSource === 'custom'}
                      onChange={() => setVideoSource('custom')}
                    />
                    Nhập link YouTube giáo viên
                  </label>
                </div>
              </div>

              {videoSource === 'system' ? (
                <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 text-sm text-blue-800 space-y-3">
                  <p className="font-medium text-blue-900">Chọn video từ hệ thống:</p>

                  {systemVideoItems.length > 0 ? (
                    <div className="mt-3 space-y-2">
                      <h5 className="font-semibold text-blue-900 mb-2">Danh sách video có sẵn:</h5>
                      <div className="space-y-2 max-h-60 overflow-y-auto">
                        {systemVideoItems.map((video, index) => {
                          const videoUrl = (video as any).src || video.content || ''
                          const isSelected = selectedSystemVideo && (
                            selectedSystemVideo.content === videoUrl ||
                            (selectedSystemVideo as any).src === videoUrl
                          )
                          return (
                            <label
                              key={index}
                              className={`flex items-start gap-3 rounded-lg border p-3 cursor-pointer transition shadow-sm ${isSelected
                                ? 'border-blue-500 bg-white ring-2 ring-blue-300'
                                : 'border-blue-200 bg-blue-50 hover:bg-white'
                                }`}
                            >
                              <input
                                type="radio"
                                className="mt-1"
                                name="system-video"
                                checked={isSelected}
                                onChange={() => setSelectedSystemVideo(video)}
                              />
                              <div className="flex-1">
                                <p className="text-sm font-semibold text-blue-900">
                                  {video.title || `Video ${index + 1}`}
                                </p>
                                {video.description && (
                                  <p className="text-xs text-blue-700 mt-1">{video.description}</p>
                                )}
                                <p className="text-xs text-blue-600 mt-1 break-all">
                                  {typeof videoUrl === 'string' ? videoUrl : 'Video URL'}
                                </p>
                              </div>
                            </label>
                          )
                        })}
                      </div>

                      {selectedSystemVideo && (
                        <div className="mt-4 bg-white rounded-lg border border-blue-200 p-3">
                          <h5 className="font-semibold text-blue-900 mb-2">Video đã chọn:</h5>
                          <ContentRenderer content={[selectedSystemVideo]} />
                        </div>
                      )}
                    </div>
                  ) : selectedSystemVideo && youtubeLinkFromMap ? (
                    <div className="mt-3">
                      <div className="bg-white rounded-lg border border-blue-200 p-3">
                        <p className="text-sm font-semibold text-blue-900 mb-2">Video từ YouTube Links:</p>
                        <ContentRenderer content={[selectedSystemVideo]} />
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-amber-700 bg-amber-50 p-3 rounded border border-amber-200">
                      <span className="text-xl">⚠️</span>
                      <p>Bài học này hiện chưa có video trên hệ thống.</p>
                    </div>
                  )}

                  <div className="pt-2 border-t border-blue-200 mt-2">
                    <button
                      onClick={() => handleOpenLessonTabs(false)}
                      className="inline-flex items-center gap-2 px-3 py-1.5 bg-white text-blue-700 border border-blue-300 rounded hover:bg-blue-50 transition-colors text-xs"
                    >
                      <Eye className="w-3 h-3" />
                      Mở bài học gốc trong tab mới
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Tiêu đề video</label>
                    <input
                      type="text"
                      value={videoTitle}
                      onChange={(e) => setVideoTitle(e.target.value)}
                      placeholder="Ví dụ: Video giải thích dao động điều hòa"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Link YouTube <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={videoLink}
                      onChange={(e) => setVideoLink(e.target.value)}
                      placeholder="https://www.youtube.com/watch?v=... hoặc https://youtu.be/..."
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      ⚠️ Chỉ chấp nhận link YouTube để tránh quá tải cho Host. Không hỗ trợ upload file video.
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Mô tả ngắn</label>
                    <textarea
                      value={videoDescription}
                      onChange={(e) => setVideoDescription(e.target.value)}
                      rows={3}
                      placeholder="Nội dung chính của video này..."
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div className="flex flex-col sm:flex-row gap-3">
                    <button
                      onClick={handleVideoLinkSave}
                      className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
                    >
                      <Upload className="w-4 h-4" />
                      Lưu link video (demo)
                    </button>
                    <button
                      onClick={() => setShowVideoPreview(!showVideoPreview)}
                      disabled={!videoLink.trim() || !isValidYouTubeLink(videoLink)}
                      className="flex-1 border border-blue-300 text-blue-700 py-2 px-4 rounded-lg hover:bg-blue-50 transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Eye className="w-4 h-4" />
                      {showVideoPreview ? 'Ẩn xem trước' : 'Xem trước'}
                    </button>
                  </div>

                  {videoLink.trim() && !isValidYouTubeLink(videoLink) && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                      <p className="text-sm text-red-700 flex items-center gap-2">
                        <XCircle className="w-4 h-4" />
                        Link không hợp lệ. Vui lòng nhập link YouTube (youtube.com hoặc youtu.be)
                      </p>
                    </div>
                  )}

                  <p className="text-xs text-gray-500">
                    * Chức năng lưu link video sẽ kết nối với TeacherContentService ở bước tiếp theo.
                  </p>

                  {showVideoPreview && videoLink.trim() && isValidYouTubeLink(videoLink) && customVideoContentItem && (
                    <div className="mt-4 bg-blue-50 border border-blue-100 rounded-lg p-4">
                      <h4 className="text-sm font-semibold text-blue-800 mb-2 flex items-center gap-2">
                        <Eye className="w-4 h-4" />
                        Preview video
                      </h4>
                      <ContentRenderer content={[customVideoContentItem]} />
                    </div>
                  )}
                </>
              )}
            </div>
          </section>

          {/* Simulation Upload */}
          <section className="bg-white rounded-xl shadow-lg border border-purple-100 p-6">
            <div className="flex items-center gap-3 mb-2">
              <span className="text-2xl">🧪</span>
              <p className="text-xs font-semibold tracking-wide text-purple-600 uppercase">Bước 3 · Video mô phỏng</p>
            </div>
            <p className="text-sm text-gray-600 mb-4">Chọn mô phỏng mặc định hoặc cung cấp video mô phỏng riêng.</p>

            <div className="space-y-4">
              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">Nguồn mô phỏng</p>
                <div className="flex flex-wrap gap-4 text-sm text-gray-700">
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="simulation-source-mode"
                      value="system"
                      checked={simulationSource === 'system'}
                      onChange={() => setSimulationSource('system')}
                    />
                    Dùng mô phỏng hệ thống
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="simulation-source-mode"
                      value="custom"
                      checked={simulationSource === 'custom'}
                      onChange={() => setSimulationSource('custom')}
                    />
                    Upload mô phỏng giáo viên
                  </label>
                </div>
              </div>

              {simulationSource === 'system' ? (
                <div className="bg-purple-50 border border-purple-100 rounded-lg p-4 text-sm text-purple-800 space-y-3">
                  <p className="font-medium text-purple-900">Chọn mô phỏng từ hệ thống:</p>

                  {systemSimulationItems.length > 0 ? (
                    <div className="mt-3 space-y-2">
                      <h5 className="font-semibold text-purple-900 mb-2">Danh sách mô phỏng có sẵn:</h5>
                      <div className="space-y-2 max-h-60 overflow-y-auto">
                        {systemSimulationItems.map((simulation, index) => {
                          const simulationUrl = (simulation as any).src || simulation.content || ''
                          const isSelected = selectedSystemSimulation && (
                            selectedSystemSimulation.content === simulationUrl ||
                            (selectedSystemSimulation as any).src === simulationUrl
                          )
                          return (
                            <label
                              key={index}
                              className={`flex items-start gap-3 rounded-lg border p-3 cursor-pointer transition shadow-sm ${isSelected
                                ? 'border-purple-500 bg-white ring-2 ring-purple-300'
                                : 'border-purple-200 bg-purple-50 hover:bg-white'
                                }`}
                            >
                              <input
                                type="radio"
                                className="mt-1"
                                name="system-simulation"
                                checked={isSelected}
                                onChange={() => setSelectedSystemSimulation(simulation)}
                              />
                              <div className="flex-1">
                                <p className="text-sm font-semibold text-purple-900">
                                  {simulation.title || `Mô phỏng ${index + 1}`}
                                </p>
                                {simulation.description && (
                                  <p className="text-xs text-purple-700 mt-1">{simulation.description}</p>
                                )}
                                <p className="text-xs text-purple-600 mt-1">
                                  Loại: {simulation.type || 'video'}
                                </p>
                              </div>
                            </label>
                          )
                        })}
                      </div>

                      {selectedSystemSimulation && (
                        <div className="mt-4 bg-white rounded-lg border border-purple-200 p-3">
                          <h5 className="font-semibold text-purple-900 mb-2">Mô phỏng đã chọn:</h5>
                          <ContentRenderer content={[selectedSystemSimulation]} />
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-amber-700 bg-amber-50 p-3 rounded border border-amber-200">
                      <span className="text-xl">⚠️</span>
                      <p>Bài học này hiện chưa có mô phỏng trên hệ thống.</p>
                    </div>
                  )}

                  <div className="pt-2 border-t border-purple-200 mt-2">
                    <button
                      onClick={() => handleOpenLessonTabs(false)}
                      className="inline-flex items-center gap-2 px-3 py-1.5 bg-white text-purple-700 border border-purple-300 rounded hover:bg-purple-50 transition-colors text-xs"
                    >
                      <Eye className="w-3 h-3" />
                      Mở bài học gốc trong tab mới
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex gap-3 text-sm">
                    <label className="flex items-center gap-2">
                      <input
                        type="radio"
                        name="simulation-upload-type"
                        value="upload"
                        checked={simulationSourceType === 'upload'}
                        onChange={() => setSimulationSourceType('upload')}
                      />
                      Upload file
                    </label>
                    <label className="flex items-center gap-2">
                      <input
                        type="radio"
                        name="simulation-upload-type"
                        value="link"
                        checked={simulationSourceType === 'link'}
                        onChange={() => setSimulationSourceType('link')}
                      />
                      Dùng link
                    </label>
                  </div>

                  {simulationSourceType === 'upload' ? (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Chọn file video mô phỏng</label>
                      <div className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${simulationUploading
                        ? 'border-gray-400 bg-gray-50'
                        : simulationServerPath
                          ? 'border-green-500 bg-green-50'
                          : 'border-gray-300 hover:border-purple-500'
                        }`}>
                        <input
                          type="file"
                          accept=".mp4,.webm,.ogg,.mov"
                          onChange={handleSimulationFileChange}
                          className="hidden"
                          id="simulation-upload"
                          disabled={simulationUploading}
                        />
                        <label
                          htmlFor="simulation-upload"
                          className={`flex flex-col items-center gap-2 ${simulationUploading ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'
                            }`}
                        >
                          {simulationUploading ? (
                            <>
                              <Loader className="w-8 h-8 text-purple-500 animate-spin" />
                              <span className="text-sm text-gray-600">Đang upload video lên server...</span>
                            </>
                          ) : simulationServerPath ? (
                            <>
                              <CheckCircle className="w-8 h-8 text-green-500" />
                              <span className="text-sm font-semibold text-green-700">
                                ✅ Đã upload: {simulationFile?.name || 'Video'}
                              </span>
                              <span className="text-xs text-green-600 mt-1">
                                Path: {simulationServerPath}
                              </span>
                            </>
                          ) : (
                            <>
                              <Upload className="w-8 h-8 text-gray-400" />
                              <span className="text-sm text-gray-600">
                                {simulationFile ? simulationFile.name : 'Click để chọn file video mô phỏng'}
                              </span>
                              {simulationFile && !simulationServerPath && (
                                <span className="text-xs text-amber-600 mt-1">
                                  ⚠️ File đã chọn nhưng chưa upload, vui lòng chọn lại để upload
                                </span>
                              )}
                            </>
                          )}
                        </label>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Link video mô phỏng</label>
                      <input
                        type="text"
                        value={simulationLink}
                        onChange={(e) => setSimulationLink(e.target.value)}
                        placeholder="https://cdn.example.com/simulation.mp4"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      />
                    </div>
                  )}

                  <div className="flex flex-col sm:flex-row gap-3">
                    <button
                      onClick={handleSimulationSubmit}
                      className="flex-1 bg-purple-600 text-white py-2 px-4 rounded-lg hover:bg-purple-700 transition-colors flex items-center justify-center gap-2"
                    >
                      <Upload className="w-4 h-4" />
                      Lưu mô phỏng (demo)
                    </button>
                    <button
                      onClick={() => setShowSimulationPreview(!showSimulationPreview)}
                      disabled={
                        (simulationSourceType === 'upload' && !simulationFile) ||
                        (simulationSourceType === 'link' && !simulationLink.trim() && !simulationPreviewUrl)
                      }
                      className="flex-1 border border-purple-300 text-purple-700 py-2 px-4 rounded-lg hover:bg-purple-50 transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Eye className="w-4 h-4" />
                      {showSimulationPreview ? 'Ẩn xem trước' : 'Xem trước'}
                    </button>
                  </div>

                  <p className="text-xs text-gray-500">
                    * Bước này mới chỉ là giao diện. Logic lưu sẽ được tích hợp sau.
                  </p>

                  {showSimulationPreview && simulationPreviewUrl && (
                    <div className="mt-4 bg-purple-50 border border-purple-100 rounded-lg p-4">
                      <h4 className="text-sm font-semibold text-purple-800 mb-2 flex items-center gap-2">
                        <Eye className="w-4 h-4" />
                        Preview video mô phỏng
                      </h4>
                      <ContentRenderer
                        content={[
                          {
                            type: 'video',
                            src: simulationPreviewUrl,
                            title: 'Video mô phỏng',
                            description: 'Preview video mô phỏng do giáo viên cung cấp'
                          } as any
                        ]}
                      />
                    </div>
                  )}
                </>
              )}
            </div>
          </section>
        </div>

        {/* Infographic Upload Section - Bước 3.5 */}
        <section className="bg-white rounded-xl shadow-lg border border-orange-100 p-6 mt-8">
          <div className="flex items-center gap-3 mb-2">
            <span className="text-2xl">🖼️</span>
            <p className="text-xs font-semibold tracking-wide text-orange-600 uppercase">Bước 3.5 · Infographic</p>
          </div>
          <p className="text-sm text-gray-600 mb-4">Upload ảnh Infographic tổng hợp kiến thức bài học.</p>

          <div className="space-y-4">
            <div>
              <p className="text-sm font-medium text-gray-700 mb-2">Nguồn Infographic</p>
              <div className="flex flex-wrap gap-4 text-sm text-gray-700">
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="infographic-source"
                    value="system"
                    checked={infographicSource === 'system'}
                    onChange={() => setInfographicSource('system')}
                  />
                  Dùng hệ thống
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="infographic-source"
                    value="custom"
                    checked={infographicSource === 'custom'}
                    onChange={() => setInfographicSource('custom')}
                  />
                  Giáo viên upload
                </label>
              </div>
            </div>

            {infographicSource === 'custom' && (
              <>
                <div className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${infographicServerPath ? 'border-green-500 bg-green-50' : 'border-gray-300 hover:border-orange-500'
                  }`}>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleInfographicFileChange}
                    className="hidden"
                    id="infographic-upload"
                    disabled={infographicUploading}
                  />
                  <label
                    htmlFor="infographic-upload"
                    className={`flex flex-col items-center gap-2 ${infographicUploading ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}
                  >
                    {infographicUploading ? (
                      <>
                        <Loader className="w-8 h-8 text-orange-500 animate-spin" />
                        <span className="text-sm text-gray-600">Đang upload...</span>
                      </>
                    ) : infographicServerPath ? (
                      <>
                        <CheckCircle className="w-8 h-8 text-green-500" />
                        <span className="text-sm font-semibold text-green-700">✅ Đã upload thành công</span>
                      </>
                    ) : (
                      <>
                        <ImageIcon className="w-8 h-8 text-gray-400" />
                        <span className="text-sm text-gray-600">Click để chọn file ảnh Infographic</span>
                      </>
                    )}
                  </label>
                </div>

                {infographicFile && !infographicServerPath && (
                  <button
                    onClick={handleInfographicSubmit}
                    className="w-full mt-4 bg-orange-500 text-white py-2 rounded-lg hover:bg-orange-600 transition-all font-semibold shadow-md flex items-center justify-center gap-2"
                    disabled={infographicUploading}
                  >
                    <Upload className="w-4 h-4" />
                    Upload Infographic
                  </button>
                )}

                {showInfographicPreview && infographicPreviewUrl && (
                  <div className="mt-4 bg-orange-50 border border-orange-100 rounded-lg p-4">
                    <h4 className="text-sm font-semibold text-orange-800 mb-2 flex items-center gap-2">
                      <Eye className="w-4 h-4" /> Preview
                    </h4>
                    <img src={infographicPreviewUrl} alt="Preview" className="max-w-full h-auto rounded shadow-sm mx-auto" style={{ maxHeight: '400px' }} />
                  </div>
                )}
              </>
            )}

            {infographicSource === 'system' && (
              <div className="bg-orange-50 border border-orange-100 p-4 rounded-lg">
                <p className="text-sm text-orange-800">Sử dụng Infographic có sẵn của hệ thống (nếu có).</p>
              </div>
            )}
          </div>
        </section>

        {/* Database Upload Section - Bước 4 */}
        <div className="mt-8">
          <div className="bg-white rounded-lg shadow-lg p-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-semibold tracking-wide text-green-600 uppercase">Bước 4 · Ngân hàng câu hỏi</p>
              <Database className="w-6 h-6 text-green-600" />
            </div>
            <p className="text-sm text-gray-600 mb-4">
              Chọn ngân hàng câu hỏi mặc định hoặc upload thư mục <code className="bg-gray-200 px-1 rounded text-gray-800">Bxx</code> (TN/DS/TL).
            </p>

            {/* Chọn nguồn database */}
            <div className="mb-4">
              <p className="text-sm font-medium text-gray-700 mb-2">Nguồn câu hỏi</p>
              <div className="flex flex-wrap gap-4 text-sm text-gray-700">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="quizSource"
                    value="system"
                    checked={quizSource === 'system'}
                    onChange={() => setQuizSource('system')}
                    className="w-4 h-4 text-blue-600 focus:ring-blue-500"
                  />
                  Dùng ngân hàng hệ thống
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="quizSource"
                    value="custom"
                    checked={quizSource === 'custom'}
                    onChange={() => setQuizSource('custom')}
                    className="w-4 h-4 text-blue-600 focus:ring-blue-500"
                  />
                  Giáo viên upload
                </label>
              </div>
            </div>

            <div className="space-y-4">
              {quizSource === 'system' ? (
                <div className="bg-gray-50 border-2 border-dashed border-gray-300 rounded-xl p-8 text-center feature-locked">
                  <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Database className="w-8 h-8 text-gray-400" />
                  </div>
                  <h4 className="text-lg font-bold text-gray-700 mb-2">Sử dụng ngân hàng câu hỏi hệ thống</h4>
                  <p className="text-sm text-gray-500 max-w-md mx-auto mb-4">
                    Hệ thống sẽ tự động lấy câu hỏi từ kho dữ liệu dựa trên thông tin bài học:
                  </p>
                  <div className="flex justify-center gap-4 text-sm font-medium text-gray-700">
                    <span className="bg-white px-3 py-1 rounded border">Lớp {sharedLop}</span>
                    <span className="bg-white px-3 py-1 rounded border">Chương {sharedChuong}</span>
                    <span className="bg-white px-3 py-1 rounded border">Bài {sharedBai}</span>
                  </div>
                </div>
              ) : (
                <div className="space-y-6">
                  {/* --- KHU VỰC UPLOAD ĐỘC LẬP (GIỐNG TESTQUIZUPLOAD) --- */}
                  <div className="bg-white rounded-xl shadow-sm border border-indigo-100 overflow-hidden">
                    <div className="bg-indigo-50 px-6 py-4 border-b border-indigo-100 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-indigo-100 rounded-lg">
                          <FolderOpen className="w-6 h-6 text-indigo-600" />
                        </div>
                        <div>
                          <h3 className="text-lg font-bold text-gray-800">Ngân hàng câu hỏi (Quiz)</h3>
                          <p className="text-xs text-gray-500">Upload folder Bxx chứa TN/DS/TL</p>
                        </div>
                      </div>

                      {/* Trạng thái Database */}
                      <div className="flex items-center gap-2">
                        {questionStats ? (
                          <span className="px-3 py-1 bg-green-100 text-green-700 text-xs font-bold rounded-full">
                            Đã có {questionStats.TN + questionStats.DS + questionStats.TL} câu
                          </span>
                        ) : (
                          <span className="px-3 py-1 bg-gray-100 text-gray-500 text-xs font-medium rounded-full">
                            Chưa có dữ liệu
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="p-6">
                      {/* 1. Nhập thông tin (Lesson Info) */}
                      <div className="grid grid-cols-3 gap-6 mb-6">
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">Lớp</label>
                          <input
                            type="text"
                            value={sharedLop}
                            onChange={(e) => setSharedLop(e.target.value)}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all font-medium"
                            placeholder="11"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">Chương</label>
                          <input
                            type="text"
                            value={sharedChuong}
                            onChange={(e) => setSharedChuong(e.target.value)}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all font-medium"
                            placeholder="1"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">Bài</label>
                          <input
                            type="text"
                            value={sharedBai}
                            onChange={(e) => setSharedBai(e.target.value)}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all font-medium"
                            placeholder="1"
                          />
                        </div>
                      </div>

                      {/* 2. Khu vực Nút Upload (Chính xác từ TestQuizUpload) */}
                      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-100 dashed-border relative group">
                        <div className="flex flex-col items-center justify-center text-center">
                          <div className="mb-4">
                            <h4 className="text-base font-bold text-indigo-900 mb-1">Upload Folder Quiz</h4>
                            <p className="text-xs text-indigo-600">Chọn thư mục Bxx (ví dụ B06) chứa các thư mục con TN, DS, TL</p>
                          </div>

                          <label className="relative inline-block cursor-pointer">
                            <input
                              type="file"
                              // @ts-ignore
                              webkitdirectory=""
                              directory=""
                              multiple
                              onChange={handleFolderUpload}
                              disabled={folderUploading}
                              className="hidden"
                            />
                            <div className={`flex items-center gap-3 px-8 py-4 rounded-xl shadow-lg transition-all transform hover:-translate-y-1 ${folderUploading
                              ? 'bg-gray-200 text-gray-500 cursor-not-allowed shadow-none'
                              : 'bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-700 hover:to-blue-700 text-white shadow-indigo-200'
                              }`}>
                              {folderUploading ? (
                                <>
                                  <Loader className="w-5 h-5 animate-spin" />
                                  <span className="font-semibold">Đang xử lý...</span>
                                </>
                              ) : (
                                <>
                                  <FolderOpen className="w-6 h-6" />
                                  <span className="font-bold text-lg">Chọn Folder Bxx</span>
                                </>
                              )}
                            </div>
                          </label>

                          <div className="mt-6 flex flex-wrap justify-center gap-3 text-xs font-mono text-gray-600 bg-white/50 p-2 rounded-lg">
                            <span className="px-2 py-1 bg-white border rounded">Bxx/TN/*.json</span>
                            <span className="text-gray-300">|</span>
                            <span className="px-2 py-1 bg-white border rounded">Bxx/DS/*.json</span>
                            <span className="text-gray-300">|</span>
                            <span className="px-2 py-1 bg-white border rounded">Bxx/TL/*.json</span>
                          </div>
                        </div>
                      </div>

                      {/* 3. Hiển thị Kết quả & Logs */}
                      {(folderUploadMessage || questionStats) && (
                        <div className="mt-6 animate-in fade-in slide-in-from-top-2">
                          <div className="bg-white border rounded-lg p-4 shadow-sm">
                            {folderUploadMessage && (
                              <div className={`flex items-start gap-3 p-3 rounded-md mb-4 ${folderUploadMessage.includes('❌') ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'
                                }`}>
                                {folderUploadMessage.includes('❌') ? <XCircle className="w-5 h-5 flex-shrink-0 mt-0.5" /> : <CheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />}
                                <div className="text-sm font-medium">{folderUploadMessage}</div>
                              </div>
                            )}

                            {questionStats && (
                              <div className="grid grid-cols-3 gap-4">
                                <div className="p-3 bg-blue-50 rounded-lg text-center border border-blue-100">
                                  <div className="text-xs text-blue-600 uppercase font-bold tracking-wider mb-1">Trắc nghiệm</div>
                                  <div className="text-2xl font-black text-blue-700">{questionStats.TN}</div>
                                </div>
                                <div className="p-3 bg-purple-50 rounded-lg text-center border border-purple-100">
                                  <div className="text-xs text-purple-600 uppercase font-bold tracking-wider mb-1">Đúng / Sai</div>
                                  <div className="text-2xl font-black text-purple-700">{questionStats.DS}</div>
                                </div>
                                <div className="p-3 bg-orange-50 rounded-lg text-center border border-orange-100">
                                  <div className="text-xs text-orange-600 uppercase font-bold tracking-wider mb-1">Tự luận</div>
                                  <div className="text-2xl font-black text-orange-700">{questionStats.TL}</div>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* 4. Nút Preview (Giống Footer của TestQuizUpload) */}
                  <button
                    onClick={handlePreviewQuestions}
                    className="w-full bg-white border-2 border-indigo-100 hover:border-indigo-300 hover:bg-indigo-50 text-indigo-700 font-bold py-3 px-6 rounded-xl transition-all flex items-center justify-center gap-2 group"
                  >
                    <Eye className="w-5 h-5 group-hover:scale-110 transition-transform" />
                    <span>Kiểm tra hiển thị (Preview)</span>
                  </button>
                </div>

              )}
            </div>
          </div>
        </div>

        {/* Upload Tổng Hợp - Lưu tất cả nội dung */}
        <div className="mt-8">
          <div className="bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 rounded-2xl shadow-2xl p-8 border-4 border-white">
            <div className="bg-white/95 backdrop-blur-sm rounded-xl p-6">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-16 h-16 bg-gradient-to-br from-indigo-600 to-purple-600 rounded-full flex items-center justify-center shadow-lg">
                  <Upload className="w-8 h-8 text-white" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-900">Upload Tổng Hợp</h3>
                  <p className="text-sm text-gray-600 mt-1">
                    Lưu toàn bộ nội dung từ 4 bước để thay thế nội dung các tab trong trang bài học
                  </p>
                </div>
              </div>

              {/* Progress Indicators */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                {[
                  { key: 'theory', label: 'Lý thuyết', icon: FileText },
                  { key: 'video', label: 'Video', icon: Eye },
                  { key: 'simulation', label: 'Mô phỏng', icon: Database },
                  { key: 'infographic', label: 'Infographic', icon: ImageIcon },
                  { key: 'quiz', label: 'Ngân hàng', icon: Database }
                ].map(({ key, label, icon: Icon }) => {
                  const status = uploadProgress[key as keyof typeof uploadProgress]
                  return (
                    <div
                      key={key}
                      className={`p-3 rounded-lg border-2 transition-all ${status === 'success'
                        ? 'bg-green-50 border-green-300'
                        : status === 'error'
                          ? 'bg-red-50 border-red-300'
                          : status === 'uploading'
                            ? 'bg-blue-50 border-blue-300 animate-pulse'
                            : 'bg-gray-50 border-gray-200'
                        }`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        {status === 'uploading' && <Loader className="w-4 h-4 animate-spin text-blue-600" />}
                        {status === 'success' && <CheckCircle className="w-4 h-4 text-green-600" />}
                        {status === 'error' && <XCircle className="w-4 h-4 text-red-600" />}
                        {status === 'pending' && <Icon className="w-4 h-4 text-gray-400" />}
                        <span className="text-xs font-semibold text-gray-700">{label}</span>
                      </div>
                      <div className="text-xs text-gray-600 capitalize">{status}</div>
                    </div>
                  )
                })}
              </div>

              {/* Upload Button */}
              <button
                onClick={handleUploadAllContent}
                disabled={uploadingAll || !sharedLop || !sharedChuong || !sharedBai}
                className="w-full bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 hover:from-indigo-700 hover:via-purple-700 hover:to-pink-700 text-white font-bold py-4 px-6 rounded-xl border-2 border-white shadow-xl hover:shadow-2xl transition-all duration-300 flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:shadow-xl"
              >
                {uploadingAll ? (
                  <>
                    <Loader className="w-6 h-6 animate-spin" />
                    <span>Đang upload...</span>
                  </>
                ) : (
                  <>
                    <Upload className="w-6 h-6" />
                    <span>Upload Tất Cả Nội Dung</span>
                  </>
                )}
              </button>

              {/* Results */}
              {uploadResults.length > 0 && (
                <div className="mt-6 bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <h4 className="text-sm font-semibold text-gray-900 mb-3">Kết quả upload:</h4>
                  <div className="space-y-1">
                    {uploadResults.map((result, index) => (
                      <p
                        key={index}
                        className={`text-sm ${result.startsWith('✅')
                          ? 'text-green-700'
                          : result.startsWith('❌')
                            ? 'text-red-700'
                            : 'text-amber-700'
                          }`}
                      >
                        {result}
                      </p>
                    ))}
                  </div>

                  {/* Buttons to view content after successful upload */}
                  {uploadResults.some(r => r.startsWith('✅')) && (
                    <div className="mt-4 pt-4 border-t border-gray-300 flex flex-col sm:flex-row gap-3">
                      <button
                        onClick={() => handleOpenLessonTabs(true)}
                        className="flex-1 bg-gradient-to-r from-blue-500 to-blue-600 text-white py-2 px-4 rounded-lg hover:from-blue-600 hover:to-blue-700 transition-all duration-200 shadow-md hover:shadow-lg flex items-center justify-center gap-2 font-semibold"
                      >
                        <Eye className="w-5 h-5" />
                        Xem bài học với nội dung đã upload
                      </button>
                      <button
                        onClick={handlePreviewQuestions}
                        className="flex-1 bg-gradient-to-r from-green-500 to-green-600 text-white py-2 px-4 rounded-lg hover:from-green-600 hover:to-green-700 transition-all duration-200 shadow-md hover:shadow-lg flex items-center justify-center gap-2 font-semibold"
                      >
                        <Eye className="w-5 h-5" />
                        Xem giao diện làm bài
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Info */}
              <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-xs text-blue-800 mb-3">
                  <strong>Lưu ý:</strong> Sau khi upload, nội dung sẽ thay thế các tab trong trang bài học cho học sinh.
                  Học sinh sẽ thấy nội dung giáo viên đã upload khi truy cập bài học với tham số <code className="bg-blue-100 px-1 rounded">?teacher={user.id}</code>
                </p>
              </div>

              {/* Verify Button */}
              <button
                onClick={async () => {
                  if (!sharedLop || !sharedChuong || !sharedBai) {
                    toast.error('Vui lòng nhập đầy đủ Lớp, Chương và Bài')
                    return
                  }
                  setVerifying(true)
                  setVerificationResult(null)
                  try {
                    const lessonId = `lop-${sharedLop}-chuong-${sharedChuong}-bai-${sharedBai}`
                    const [contentResult, databasesResult] = await Promise.all([
                      teacherContentService.verifySavedContent(user.id, lessonId),
                      teacherContentService.verifyUploadedDatabases(user.id, sharedLop, sharedChuong, sharedBai)
                    ])
                    setVerificationResult({
                      content: contentResult,
                      databases: databasesResult
                    })
                    toast.success('✅ Đã kiểm tra dữ liệu đã lưu')
                  } catch (error: any) {
                    console.error('❌ Error verifying data:', error)
                    toast.error(`Lỗi khi kiểm tra: ${error?.message || 'Unknown error'}`)
                  } finally {
                    setVerifying(false)
                  }
                }}
                disabled={verifying || !sharedLop || !sharedChuong || !sharedBai}
                className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white font-semibold py-3 px-6 rounded-xl border-2 border-white shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center gap-3 mt-4 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {verifying ? (
                  <>
                    <Loader className="w-5 h-5 animate-spin" />
                    <span>Đang kiểm tra...</span>
                  </>
                ) : (
                  <>
                    <Database className="w-5 h-5" />
                    <span>Kiểm tra dữ liệu đã lưu</span>
                  </>
                )}
              </button>

              {/* Verification Results */}
              {verificationResult && (
                <div className="mt-6 bg-gradient-to-br from-cyan-50 to-blue-50 border-2 border-cyan-200 rounded-xl p-6">
                  <h4 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <Database className="w-5 h-5 text-cyan-600" />
                    Kết quả kiểm tra dữ liệu
                  </h4>

                  {/* Content Verification */}
                  <div className="mb-6">
                    <h5 className="text-sm font-semibold text-gray-800 mb-3">📚 Nội dung đã lưu (WordPress Database):</h5>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {[
                        { key: 'theory', label: 'Lý thuyết', icon: FileText },
                        { key: 'video', label: 'Video', icon: Eye },
                        { key: 'simulation', label: 'Mô phỏng', icon: Database },
                        { key: 'infographic', label: 'Infographic', icon: ImageIcon },
                        { key: 'quiz', label: 'Ngân hàng', icon: Database }
                      ].map(({ key, label, icon: Icon }) => {
                        const exists = verificationResult.content[key as keyof typeof verificationResult.content] as boolean
                        const detail = verificationResult.content.details[key]
                        return (
                          <div
                            key={key}
                            className={`p-3 rounded-lg border-2 ${exists
                              ? 'bg-green-50 border-green-300'
                              : 'bg-gray-50 border-gray-200'
                              }`}
                          >
                            <div className="flex items-center gap-2 mb-1">
                              {exists ? (
                                <CheckCircle className="w-4 h-4 text-green-600" />
                              ) : (
                                <XCircle className="w-4 h-4 text-gray-400" />
                              )}
                              <Icon className="w-4 h-4 text-gray-600" />
                              <span className="text-xs font-semibold text-gray-700">{label}</span>
                            </div>
                            <div className="text-xs text-gray-600">
                              {exists ? '✅ Đã lưu' : '❌ Chưa lưu'}
                            </div>
                            {detail?.updatedAt && (
                              <div className="text-xs text-gray-500 mt-1">
                                {new Date(detail.updatedAt).toLocaleDateString('vi-VN')}
                              </div>
                            )}
                            {detail?.source && (
                              <div className={`text-xs mt-1 ${detail.source === 'server' ? 'text-green-600 font-semibold' : 'text-amber-600'
                                }`}>
                                {detail.source === 'server' ? '🌐 Server' : '💾 LocalStorage'}
                              </div>
                            )}
                          </div>
                        )
                      })}
                    </div>
                  </div>

                  {/* Database Verification */}
                  <div>
                    <h5 className="text-sm font-semibold text-gray-800 mb-3">💾 Databases đã upload (IndexedDB):</h5>
                    <div className="grid grid-cols-3 gap-3">
                      {[
                        { key: 'TN', label: 'Trắc nghiệm', color: 'blue' },
                        { key: 'DS', label: 'Đúng/Sai', color: 'purple' },
                        { key: 'TL', label: 'Tự luận', color: 'orange' }
                      ].map(({ key, label, color }) => {
                        const dbInfo = verificationResult.databases[key as 'TN' | 'DS' | 'TL']
                        return (
                          <div
                            key={key}
                            className={`p-3 rounded-lg border-2 ${dbInfo.exists
                              ? 'bg-green-50 border-green-300'
                              : 'bg-gray-50 border-gray-200'
                              }`}
                          >
                            <div className="flex items-center gap-2 mb-1">
                              {dbInfo.exists ? (
                                <CheckCircle className="w-4 h-4 text-green-600" />
                              ) : (
                                <XCircle className="w-4 h-4 text-gray-400" />
                              )}
                              <span className="text-xs font-semibold text-gray-700">{label}</span>
                            </div>
                            {dbInfo.exists ? (
                              <>
                                <div className="text-xs text-green-700">✅ Đã upload</div>
                                <div className={`text-lg font-bold text-${color}-600 mt-1`}>
                                  {dbInfo.questionCount} câu
                                </div>
                              </>
                            ) : (
                              <div className="text-xs text-gray-500">❌ Chưa upload</div>
                            )}
                          </div>
                        )
                      })}
                    </div>
                  </div>
                </div>
              )}

              {/* Button to view lesson with teacher content */}
              {sharedLop && sharedChuong && sharedBai && (
                <button
                  onClick={() => {
                    // Route đúng: /lop/:idLop/chuong/:idChuong/bai/:idBai
                    const lopParam = sharedLop.replace(/^lop-?/i, '').replace(/[^0-9]/g, '') || '10'
                    const chuongParam = sharedChuong.replace(/^chuong-?/i, '').replace(/[^0-9]/g, '') || '1'
                    // Giữ nguyên format của idBai (có thể là "bai-1" hoặc "1")
                    let baiParam = sharedBai.replace(/^bai-?/i, '')
                    // Nếu chỉ là số, thêm prefix "bai-"
                    if (/^\d+$/.test(baiParam)) {
                      baiParam = `bai-${baiParam}`
                    } else if (!baiParam.startsWith('bai-')) {
                      baiParam = `bai-${baiParam}`
                    }
                    const lessonUrl = `/lop/${lopParam}/chuong/${chuongParam}/bai/${baiParam}?teacher=${user.id}`
                    console.log('🔗 Opening lesson URL:', lessonUrl)
                    window.open(lessonUrl, '_blank', 'noopener,noreferrer')
                  }}
                  className="w-full bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white font-semibold py-3 px-6 rounded-xl border-2 border-white shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center gap-3 mt-4"
                >
                  <Eye className="w-5 h-5" />
                  <span>Xem bài học với nội dung đã upload</span>
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>



  )
}



function cloneLessonData(data: LessonData): LessonData {
  return JSON.parse(JSON.stringify(data))
}

export default TestTeacherContent
